import {
  AutoInsuranceInfoInput,
  CompressionLineItemInput,
  CompressionOrderInput,
  CpapOrderInput,
  CustomOrthoticsOrderInput,
  Department,
  DepartmentCategories,
  DiabetesOrderInput,
  Diagnosis,
  DiagnosisInput,
  DiagnosisType,
  DiagnosisTypeEnum,
  FaxInput,
  FormOptionItem,
  InputMaybe,
  MiscellaneousDiagnosisInput,
  MiscellaneousLineItemInput,
  ModernLineItem,
  ModernLineItemInput,
  NebulizerAdministrationSetType,
  NebulizerCompressorType,
  NebulizerMedication,
  NebulizerOrderInput,
  OffTheShelfOrthoticsOrderInput,
  OstomyCheckboxAttributeInput,
  OstomyLineItemInput,
  OstomyOrderInput,
  PatientInsuranceInfoInput,
  PlanOfCare,
  PlanOfCareInput,
  ProductSizeDetails,
  ProstheticsOrderInput,
  ProviderReferralInput,
  ShellFormOrderInput,
  UrologyCheckboxAttributeInput,
  UrologyLineItemInput,
  UrologyOrderInput,
  VentilatorInterfaceType,
  VentilatorOrderInput,
  WheelchairsAndScootersDeviceType,
  WheelchairsAndScootersOrderInput,
  WorkersCompInfoInput,
  Wound,
  WoundCareLineItem,
  WoundCareLineItemInput,
  WoundInput,
  WoundSuctionCanisterType,
  WoundSuctionMachineType,
  WoundSuctionOrderInput,
  WoundSuctionTreatment,
  WoundSuctionTreatmentInput,
} from "../__generated__/graphql";
import {
  DEFAULT_MEDICARE_WOUND_PREFERENCE_ID,
  NEBULIZER_MEDICATION_LABELS,
  NUM_MISCELLANEOUS_DIAGNOSES,
  NUM_MISCELLANEOUS_LINE_ITEMS,
  NUM_WOUNDS,
  WHEELCHAIRS_AND_SCOOTERS_DEVICE_ITEM_NAMES,
  WHEELCHAIRS_AND_SCOOTERS_OTHER_DEVICE_TYPES,
} from "./constants";

import { SHELL_FORM_CATEGORIES } from "./shellFormConfig";
import cloneDeep from "lodash/cloneDeep";
import { isOrderedCustomOrthoticsItem } from "./customOrthoticsLabels";
import { traverse } from "object-traversal";

/**
 * Helper function to recursively remove __typename fields from GraphQL output data
 * for use as input data. Used with object-traversal library.
 *
 * Usage: traverse(yourObject, removeTypenameTraverser);
 */
export const removeTypenameTraverser = ({
  parent,
  key,
}: {
  parent: Record<string, unknown> | null;
  key: string | null;
}) => {
  if (key === "__typename") {
    delete (parent as any)?.[key];
  }
};

export type CategoryLineItems = {
  [Category.WoundCare]: WoundCareLineItemInput;
} & CategoryLineItemsNoWoundCare;

export interface CategoryLineItemsNoWoundCare {
  [Category.Urology]: UrologyLineItemInput;
  [Category.Ostomy]: OstomyLineItemInput;
}

export type CategoryTypes = keyof CategoryLineItems;

export enum SelectedCompressionItemsEnum {
  selectedWrapCompressionLineItem = "selectedWrapCompressionLineItem",
  selectedStockingCompressionLineItem = "selectedStockingCompressionLineItem",
  selectedArmSleeveCompressionLineItem = "selectedArmSleeveCompressionLineItem",
  selectedCompressionBraLineItem = "selectedCompressionBraLineItem",
}

export interface SelectedCompressionLineItems {
  [SelectedCompressionItemsEnum.selectedWrapCompressionLineItem]: Array<CompressionLineItemInput>;
  [SelectedCompressionItemsEnum.selectedStockingCompressionLineItem]: Array<CompressionLineItemInput>;
  [SelectedCompressionItemsEnum.selectedArmSleeveCompressionLineItem]: Array<CompressionLineItemInput>;
  [SelectedCompressionItemsEnum.selectedCompressionBraLineItem]: Array<CompressionLineItemInput>;
}

export enum Category {
  WoundCare = "WOUND_CARE",
  Urology = "UROLOGY",
  Ostomy = "OSTOMY",
  Compression = "COMPRESSION",
  Miscellaneous = "MISCELLANEOUS_PRODUCTS",
  CareBridge = "CARE_BRIDGE",
  DiabeticSupplies = "DIABETIC_SUPPLIES",
  HospitalBed = "HOSPITAL_BED",
  Walker = "WALKER",
  Wheelchair = "WHEELCHAIR",
  Oxygen = "OXYGEN",
  BathingAndBathroom = "BATHING_AND_BATHROOM",
  EnteralNutrition = "ENTERAL_NUTRITION",
  Incontinence = "INCONTINENCE",
  PostMastectomy = "POST_MASTECTOMY",
  ProstheticsAndOrthotics = "PROSTHETICS_AND_ORTHOTICS",
  Cpap = "CPAP",
  PneumaticCompression = "PNEUMATIC_COMPRESSION",
  Nebulizer = "NEBULIZER",
  OffTheShelfOrthotics = "OFF_THE_SHELF_ORTHOTICS",
  WoundSuction = "WOUND_SUCTION",
  CoughStimulating = "COUGH_STIMULATING",
  Tracheostomy = "TRACHEOSTOMY",
  ElectricalStimulators = "ELECTRICAL_STIMULATORS",
  CustomOrthotics = "CUSTOM_ORTHOTICS",
  WheelchairsAndScooters = "WHEELCHAIRS_AND_SCOOTERS",
  Diabetes = "DIABETES",
  Ventilator = "VENTILATOR",
  Prosthetics = "PROSTHETICS",
}

const STOP_ORDER_INSURANCES = [
  "veterans plan",
  "champva/spina bifida/children of women vietnam vets",
  "us dept of veterans affairs",
  "veterans affairs fee basis program",
  "veteran administrators (va) healthcare",
  "veterans administrations",
];

// Substrings that indicate a non-traditional Medicare plan (Advantage,
// Supplement, Medicaid, etc.). Mirrors MEDICARE_INSURANCE_NAME_EXCLUSION_SUBSTRINGS
// in services/orders/constants.py — keep the two lists in sync.
const MEDICARE_INSURANCE_NAME_EXCLUSION_SUBSTRINGS = [
  "advantage",
  "adv",
  "advant",
  "advantag",
  "advantge",
  "advanatage",
  "advantage2",
  "medicareadvantagepffs",
  "medadvantage",
  "advantra",
  "managed",
  "mco",
  "medicaid",
  "supplement",
  "medigap",
  "hmo",
  "ppo",
  "part c",
  "part d",
  "replacement",
  "complete",
  "plus",
  "integrated",
  "dual",
  "snp",
  "dsnp",
  "csnp",
  "isnp",
  "mapd",
  "pffs",
  "mmp",
  "fide",
  "egwp",
  "msa",
  "cost",
  "aarp",
  "aetna",
  "alignment",
  "allwell",
  "amerihealth",
  "anthem",
  "avmed",
  "bcbs",
  "blue",
  "blue cross",
  "blue shield",
  "bravo",
  "capital",
  "careplus",
  "careoregon",
  "caresource",
  "ccx medicare gold card",
  "cdphp",
  "cigna",
  "clover",
  "centene",
  "community care",
  "connecticare",
  "devoted",
  "elevance",
  "emblem",
  "essentia",
  "fidelis",
  "freedom health",
  "geisinger",
  "hap",
  "health first",
  "healthfirst",
  "healthnet",
  "healthpartners",
  "healthspring",
  "highmark",
  "humana",
  "intermountain",
  "kaiser",
  "leon health",
  "medica",
  "mediblue",
  "meridian",
  "metroplus",
  "molina",
  "mutual of omaha",
  "mvp",
  "mynexus",
  "network health",
  "oscar",
  "paramount",
  "premera",
  "presbyterian",
  "priority health",
  "regence",
  "scan",
  "selecthealth",
  "sentara",
  "summacare",
  "texanplus",
  "tufts",
  "ucare",
  "uhc",
  "united",
  "unitedhealthcare",
  "upmc",
  "wellbe",
  "wellcare",
  "wellmed",
  "wellpoint",
  "zing health",
];

// Word-boundary regex so short exclusion tokens like "hmo"/"ppo" don't match
// inside unrelated words (e.g. "Richmond Medicare" should still be Medicare).
const MEDICARE_INSURANCE_EXCLUSION_REGEX = new RegExp(
  `\\b(?:${MEDICARE_INSURANCE_NAME_EXCLUSION_SUBSTRINGS.join("|")})\\b`,
);

// Medicare Administrative Contractor (MAC) names. Some clinics enter the MAC
// that processes their Traditional Medicare claims (e.g. "Palmetto GBA")
// instead of a name containing "medicare". Mirrors
// MEDICARE_INSURANCE_NAME_INCLUSION_SUBSTRINGS in services/orders/constants.py
// — keep the two lists in sync. Exclusions above always take precedence.
const MEDICARE_INSURANCE_NAME_INCLUSION_SUBSTRINGS = [
  "palmetto",
  "novitas",
  "noridian",
  "national government services",
  "ngs",
  "dmerc",
  "first coast",
];

// Word-boundary regex matching MAC names so names without the word "medicare"
// are still recognized as Traditional Medicare.
const MEDICARE_INSURANCE_INCLUSION_REGEX = new RegExp(
  `\\b(?:${MEDICARE_INSURANCE_NAME_INCLUSION_SUBSTRINGS.join("|")})\\b`,
);

// Helpers:
export function isMedicareInsurance(primaryInsurance: string) {
  const normalized = primaryInsurance.trim().toLowerCase();
  if (
    !normalized.includes("medicare") &&
    !MEDICARE_INSURANCE_INCLUSION_REGEX.test(normalized)
  ) {
    return false;
  }
  return !MEDICARE_INSURANCE_EXCLUSION_REGEX.test(normalized);
}

// Payer names that mean Zing Health. Anchored at a word start so "amazing"
// does not match but "ZingHealth" does. Extend once Zing supplies its
// plan-name list. Mirrors ZING_INSURANCE_NAME_SUBSTRINGS in
// services/orders/zing_helper.py.
const ZING_INSURANCE_NAME_SUBSTRINGS = ["zing"];
const ZING_INSURANCE_REGEX = new RegExp(
  `\\b(?:${ZING_INSURANCE_NAME_SUBSTRINGS.join("|")})`,
);

export function isZingInsurance(primaryInsurance: string) {
  return ZING_INSURANCE_REGEX.test(primaryInsurance.trim().toLowerCase());
}

// Insurance-type dropdown values whose plans cover Incontinence supplies.
// Mirrors INCONTINENCE_COVERED_INSURANCE_TYPES in
// services/orders/incontinence_coverage.py.
const INCONTINENCE_COVERED_INSURANCE_TYPES = [
  "Medicaid",
  "Managed Medicaid",
  "D-SNP",
  // Avita's HL7 plan-type code for Medicaid, carried onto orders from its
  // patient records.
  "CAID",
];

// Payer-name substrings that mean Medicaid/D-SNP. Mirrors
// INCONTINENCE_COVERAGE_KEYWORDS in services/orders/incontinence_coverage.py
// — keep the two lists in sync.
const INCONTINENCE_COVERAGE_KEYWORDS = [
  "medicaid",
  "medi-cal",
  "medi cal",
  "mainecare",
  "dual complete",
  "dual secondary",
  "dc dual",
  "community plan",
  "community health",
  "caresource",
  "molina",
  "wellcare",
  "superior health",
  "mo healthnet",
  "nebraska total care",
  "arizona complete health",
  "south carolina healthy connections",
  "wellmed",
  "uhc medicaid",
  "medicaid prime",
];

// Dropdown values for plans that don't cover Incontinence. Only these override
// a primary-name keyword; any other stored type (integration codes, extracted
// free text such as "MANAGED CARE") is treated as unset. Mirrors
// INCONTINENCE_NOT_COVERED_INSURANCE_TYPES in services/orders/incontinence_coverage.py.
const INCONTINENCE_NOT_COVERED_INSURANCE_TYPES = [
  "Commercial",
  "Medicare advantage",
  "Other",
];

// D-SNP plan names ("DSNP", "D-SNP", "D SNP"), as a whole word so "Gold-SNP"
// and "Preferred SNP" don't match. Mirrors D_SNP_PATTERN in
// services/orders/incontinence_coverage.py.
const D_SNP_REGEX = /\bd[- ]?snp\b/;

/**
 * True when the patient's plan covers Incontinence supplies (Medicaid, Managed
 * Medicaid or D-SNP): a covered insurance type, or a keyword in the secondary
 * insurance name, whatever the type. Otherwise a non-covered dropdown type
 * blocks, and failing that a keyword in the primary name decides. The type is
 * ignored for traditional Medicare primaries, where the form usually hides the
 * dropdown and a stale value may linger. Mirrors
 * `is_incontinence_covered_insurance` in services/orders/incontinence_coverage.py.
 */
export function isIncontinenceCoveredInsurance(
  insuranceInfo:
    | {
        primaryInsuranceType?: string | null;
        primaryInsurance?: string | null;
        secondaryInsurance?: string | null;
      }
    | null
    | undefined,
) {
  const primaryInsurance = insuranceInfo?.primaryInsurance ?? "";
  const insuranceType = insuranceInfo?.primaryInsuranceType;
  const hasKeyword = (name: string | null | undefined) => {
    const normalized = (name ?? "").trim().toLowerCase();
    return (
      D_SNP_REGEX.test(normalized) ||
      INCONTINENCE_COVERAGE_KEYWORDS.some((keyword) =>
        normalized.includes(keyword),
      )
    );
  };
  const typeApplies = !!insuranceType && !isMedicareInsurance(primaryInsurance);
  if (
    typeApplies &&
    INCONTINENCE_COVERED_INSURANCE_TYPES.includes(insuranceType)
  ) {
    return true;
  }
  if (hasKeyword(insuranceInfo?.secondaryInsurance)) {
    return true;
  }
  if (
    typeApplies &&
    INCONTINENCE_NOT_COVERED_INSURANCE_TYPES.includes(insuranceType)
  ) {
    return false;
  }
  return hasKeyword(primaryInsurance);
}

export function isStopOrderInsurance(primaryInsurance: string) {
  return STOP_ORDER_INSURANCES.includes(primaryInsurance.trim().toLowerCase());
}

export function isHumanaInsurance(
  primaryInsurance: string,
  shippingState: string,
) {
  return (
    (shippingState.trim().toLowerCase() === "hi" ||
      shippingState.trim().toLowerCase() === "hawaii") &&
    primaryInsurance.trim().toLowerCase().includes("humana")
  );
}

// UHC AZ holds an exclusive DME contract with Preferred Homecare, who does
// not properly intake orders auto-referred from Verse. Keyword variants
// clinics may enter for United Healthcare.
const UHC_INSURANCE_NAME_SUBSTRINGS = [
  "uhc",
  "unitedhealthcare",
  "united health",
  "united healthcare",
];

export function isUhcArizonaInsurance(
  primaryInsurance: string,
  shippingState: string,
) {
  const normalizedInsurance = primaryInsurance.trim().toLowerCase();
  const normalizedState = shippingState.trim().toLowerCase();
  return (
    (normalizedState === "az" || normalizedState === "arizona") &&
    UHC_INSURANCE_NAME_SUBSTRINGS.some((substring) =>
      normalizedInsurance.includes(substring),
    )
  );
}

export const objectFieldSet = (
  object: any,
  fieldPath: string,
  fieldValue: unknown,
) => {
  const newObject = { ...object };
  let pathIterator = newObject;
  const pathArray = fieldPath.split(".");
  pathArray.forEach((field, i) => {
    if (i !== pathArray.length - 1) {
      const newNestedObject = { ...pathIterator[field] };
      pathIterator[field] = newNestedObject;
      pathIterator = pathIterator[field];
    }
  });

  pathIterator[pathArray[pathArray.length - 1]] = fieldValue;

  return newObject;
};
export function filterRecommendedSupplies<T extends CategoryTypes>(
  category: Category,
  emptyItem: CategoryLineItems[T],
  lineItems: any[],
  standardItems: FormOptionItem[],
) {
  const newLineItems: any[] = [];
  for (let i = 0; i < lineItems.length; i++) {
    const lineItem = lineItems[i];
    const customIndexStart = standardItems.length;
    const productIsCustom = i >= customIndexStart;
    let keep = false;

    if (category == Category.WoundCare) {
      if (!lineItemEmpty(lineItem, standardItems, i)) {
        keep = true;
      }
      if (keep) {
        newLineItems.push(lineItem);
      }
    } else {
      const itemKeysToCheck = Object.keys(emptyItem)
        .filter((key) => {
          return !["woundIndicesUsed", "formItemId"].includes(key);
        })
        .map((key) => key as keyof typeof emptyItem);
      for (const key of itemKeysToCheck) {
        const value = lineItem[key];

        const customNameChange =
          key === "productName" && productIsCustom && value !== "";

        if (customNameChange) {
          keep = true;
        } else if (
          (key !== "productName" &&
            key !== "checkboxAttributes" &&
            JSON.stringify(value) !== JSON.stringify(emptyItem[key]) &&
            (Boolean(value) || Boolean(emptyItem[key]))) ||
          (key === "checkboxAttributes" &&
            Object.values(value).some((x) => x === true))
        ) {
          keep = true;
        }
      }
      if (keep) {
        newLineItems.push(lineItem);
      }
    }
  }

  return newLineItems;
}

// Initializers:
export function initialWoundItem({
  productName,
  formItemId,
  extracted = false,
  disableQuantityAutocalculationDefaultQuantity = null,
  size = {
    id: null,
    name: "Fit to wound",
  },
  frequencyOfUse = "",
  durationOfUse = "",
  quantity = "",
  isBordered = false,
  dressingCategory,
}: {
  productName: string;
  formItemId: number | null;
  extracted?: boolean;
  disableQuantityAutocalculationDefaultQuantity?: number | null;
  size?: ProductSizeDetails;
  frequencyOfUse?: string;
  durationOfUse?: string;
  quantity?: string;
  isBordered?: boolean;
  dressingCategory?: string;
}): WoundCareLineItemInput {
  return {
    extracted: extracted,
    durationOfUse: durationOfUse,
    frequencyOfUse: frequencyOfUse,
    productName: productName,
    productNameFromFax: "",
    formItemId: formItemId,
    quantity: quantity,
    size: size,
    pinItem: false,
    woundIndicesUsed: [],
    sizePreferenceId: null,
    frequencyPreferenceId: null,
    substitutionPreferenceId: null,
    selectedWoundsPreferenceIds: [],
    disableQuantityAutocalculationDefaultQuantity,
    maxAllowedQuantity: null,
    offerExcessAsOop: null,
    quantityAutoSetToMaxAllowed: null,
    isBordered: isBordered,
    dressingCategory: dressingCategory,
  };
}

export function initialCheckboxAttributes<T extends { [key: string]: any }>(
  display: string[],
  attributes: T = {} as T,
): T {
  const intializeAttributes = {} as T;
  display.forEach((item: string) => {
    intializeAttributes[item as keyof T] = attributes[item] || false;
  });
  return intializeAttributes;
}

export function initialUrologyItem(
  productName: string,
  formItemId: number | null,
  display: string[],
): UrologyLineItemInput {
  return {
    productName: productName,
    formItemId: formItemId,
    checkboxAttributes:
      initialCheckboxAttributes<UrologyCheckboxAttributeInput>(display),
    brandPreference: "",
    frenchSizeLength: "",
    frequencyOfUse: "",
    qtyMo: "",
  };
}

export function initialOstomyItem(
  productName: string,
  formItemId: number | null,
  display: string[],
): OstomyLineItemInput {
  return {
    productName: productName,
    formItemId: formItemId,
    checkboxAttributes:
      initialCheckboxAttributes<OstomyCheckboxAttributeInput>(display),
    brandPreference: "",
    productNum: "",
    dailyFrequencyOfUse: "",
    qtyMo: "",
  };
}

export function initialMiscellaneousLineItem(): MiscellaneousLineItemInput {
  return {
    productName: "",
    sizeSpecification: "",
    quantity: 0,
    remarks: "",
  };
}

export function initialMiscellaneousDiagnosis(): MiscellaneousDiagnosisInput {
  return {
    diagnosis: "",
  };
}

export function initialInsuranceInfo(): PatientInsuranceInfoInput {
  return {
    primaryInsuranceType: null,
    primaryInsurance: null,
    primaryGroupNumber: null,
    primaryPolicyNumber: null,
    secondaryInsurance: null,
    secondaryGroupNumber: null,
    secondaryPolicyNumber: null,
  };
}

export function initialWorkersCompInfo(): WorkersCompInfoInput {
  return {
    insuranceName: null,
    claimNumber: null,
    caseManagerName: null,
    caseManagerPhone: null,
    claimAddress: null,
    dateOfAccident: null,
  };
}

export function initialAutoInsuranceInfo(): AutoInsuranceInfoInput {
  return {
    insuranceName: null,
    claimNumber: null,
    caseManagerName: null,
    caseManagerPhone: null,
    claimAddress: null,
    dateOfAccident: null,
    stateOfAccident: null,
  };
}

export function initializeFaxInput(): FaxInput {
  return {
    filename: null,
    newOrder: null,
    patientDemographics: null,
    partialProgressNote: null,
    progressNotes: null,
    signature: null,
    otherFiles: null,
  };
}

export const initializeDiagnosisInput = (
  categoryDiagnosisTypeId: number,
): DiagnosisInput => ({
  categoryDiagnosisTypeId,
  icd10DescriptionId: null,
  customIcd10Description: null,
  customIcd10Code: null,
});

/**
 * Initializes blank custom diagnosis entries for all categories that support them.
 * Ensures each category has the correct number of empty diagnosis slots available.
 */
export const initializeBlankCustomDiagnoses = (
  departmentCategories: DepartmentCategories | null,
  existingDiagnosisInputs: DiagnosisInput[],
): DiagnosisInput[] => {
  const diagnosisInputs = [...existingDiagnosisInputs];

  for (const category of departmentCategories?.categories ?? []) {
    for (const categoryDiagnosisType of category.categoryDiagnosisTypes) {
      // Count existing custom diagnoses for this category that are already filled
      const numberOfAdditionalDiagnosisPresent = diagnosisInputs.filter(
        (diagnosisInput) =>
          diagnosisInput.categoryDiagnosisTypeId === categoryDiagnosisType.id &&
          categoryDiagnosisType.diagnosisType.type ===
            DiagnosisTypeEnum.Icd10Diagnosis &&
          diagnosisInput.customIcd10Description,
      );

      // Calculate how many more blank entries are needed
      const neededBlankEntries =
        categoryDiagnosisType.numberOfCustomDiagnosis -
        numberOfAdditionalDiagnosisPresent.length;

      // Create blank entries to fill the gap
      Array.from({ length: neededBlankEntries }, () =>
        diagnosisInputs.push(
          initializeDiagnosisInput(categoryDiagnosisType.id),
        ),
      );
    }
  }

  return diagnosisInputs;
};

export function emptyWound(index: number): WoundInput {
  return {
    index: index,
    description: "",
    thickness: "",
    location: "",
    dimensionLength: "",
    dimensionWidth: "",
    dimensionDepth: "",
    amountExudate: "",
    debrided: "",
    traceId: crypto.randomUUID(),
  };
}

// Empty checkers:
export const lineItemEmpty = (
  lineItem: WoundCareLineItemInput | undefined,
  standardItems: FormOptionItem[],
  index: number,
) => {
  if (lineItem == null) {
    return true;
  }

  let empty = true;
  for (const [key, value] of Object.entries(lineItem)) {
    if (["selectedWoundsPreferenceIds", "dressingCategory"].includes(key)) {
      continue;
    } else if (key == "size") {
      if (
        (value as any).name?.length > 0 &&
        (value as any).name !== "Fit to wound"
      ) {
        empty = false;
      }
    } else if (key === "extracted" && value) {
      empty = false;
    } else if (
      (key != "productName" && key != "productNameFromFax") ||
      index >= standardItems.length
    ) {
      if (value != null && (value as any).length > 0) {
        empty = false;
      }
    }
  }

  return empty;
};

export function isWoundEmpty(wound: WoundInput): boolean {
  const { traceId: _traceId, ...woundWithoutTraceId } = wound;
  const { traceId: _emptyTraceId, ...emptyWithoutTraceId } = emptyWound(
    wound.index,
  );
  return (
    JSON.stringify(woundWithoutTraceId) === JSON.stringify(emptyWithoutTraceId)
  );
}

export function isEmptyUrologyForm(
  urologyOrder?: UrologyOrderInput | null,
): boolean {
  if (!urologyOrder) {
    return true;
  }
  const { urologyLineItems, startDate, ...otherKeys } = urologyOrder;
  if (
    !Object.values(otherKeys).every(
      (x) => x === "" || x === null || x === false,
    )
  ) {
    return false;
  }
  for (let i = 0; i < urologyLineItems.length; i++) {
    const {
      productName,
      formItemId,
      checkboxAttributes,
      ...otherLineItemKeys
    } = urologyLineItems[i];
    const isCustomItem = !formItemId;
    if (isCustomItem && productName !== "") {
      return false;
    }
    if (
      !Object.values(otherLineItemKeys).every(
        (x: any) => x === "" || x === false,
      )
    ) {
      return false;
    }
  }
  return true;
}

export const isEmptyOstomyForm = (
  ostomyOrder?: OstomyOrderInput | null,
): boolean => {
  if (!ostomyOrder) {
    return true;
  }
  const { ostomyLineItems, startDate, ...otherKeys } = ostomyOrder;
  if (
    !Object.values(otherKeys).every(
      (x) => x === "" || x === null || x === false,
    )
  ) {
    return false;
  }
  for (let i = 0; i < ostomyLineItems.length; i++) {
    const {
      productName,
      formItemId,
      checkboxAttributes,
      ...otherLineItemKeys
    } = ostomyLineItems[i];
    const isCustomItem = !formItemId;
    if (isCustomItem && productName !== "") {
      return false;
    }
    if (
      !Object.values(otherLineItemKeys).every(
        (x: any) => x === "" || x === false,
      )
    ) {
      return false;
    }
  }
  return true;
};

export const isEmptyCpapForm = (cpapOrder?: CpapOrderInput | null): boolean => {
  if (!cpapOrder) {
    return true;
  }
  const { cpapLineItems, cpapDiagnoses, ...otherKeys } = cpapOrder;
  if (
    !Object.values(otherKeys).every(
      (x) => x === "" || x === null || x === false,
    )
  ) {
    return false;
  }
  if (cpapLineItems && cpapLineItems.length > 0) {
    return false;
  }
  if (cpapDiagnoses && cpapDiagnoses.length > 0) {
    return false;
  }
  return true;
};

/**
 * A shell form line item counts as a product only if it names one — size,
 * quantity and remarks describe a product rather than standing in for it.
 * Mirrors `insert_shell_form_orders`, `validate_shell_form` and the product half
 * of the sign-ability SQL; a row this rejects never persists, so every TS surface
 * must judge rows through this and not its own inline check.
 * Typed structurally so both the Input and the query-output row types satisfy it.
 */
export const isNamedShellFormLineItem = (lineItem: {
  productName?: string | null;
}): boolean => !!lineItem.productName?.trim();

/**
 * A diagnosis row counts if it carries a description or an ICD-10 code.
 *
 * Form-agnostic on purpose: every form that captures `{diagnosis, icd10Code}`
 * rows pads its table with blanks, and all of them need the same rule for which
 * rows are real. Typed structurally so both the Input and query-output row types
 * satisfy it — reach for this rather than adding a per-form copy.
 */
export const isFilledDiagnosisRow = (diagnosis: {
  diagnosis?: string | null;
  icd10Code?: string | null;
}): boolean => !!diagnosis.diagnosis?.trim() || !!diagnosis.icd10Code?.trim();

/**
 * Enum-backed options matched on `systemKey`, in catalog order. A withdrawn
 * `selected` value keeps its option, last, since insert still ships it.
 */
export const catalogOptions = <T extends string>(
  items: FormOptionItem[],
  valueBySystemKey: Record<string, T>,
  labels: Record<T, string>,
  selected?: T | null,
): { value: T; label: string }[] => {
  const options = items.flatMap((item) => {
    const value = item.systemKey ? valueBySystemKey[item.systemKey] : undefined;
    return value === undefined
      ? []
      : [{ value, label: labels[value] ?? item.name }];
  });
  const withdrawnSelection =
    selected != null &&
    Object.values(valueBySystemKey).includes(selected) &&
    !options.some((option) => option.value === selected);
  return withdrawnSelection
    ? [...options, { value: selected, label: labels[selected] ?? selected }]
    : options;
};

/**
 * True when the provider has entered nothing clinical on a shell form.
 *
 * Field-by-field on purpose rather than "is every remaining key at its
 * default": the optional coordination details (preferred DME) must NOT count as
 * content, and neither must `productCategory`, which the store writes the moment
 * the form is touched. On their own they carry nothing to fulfil, and the backend
 * submit predicate (`shell_form_started`), the sign-ability SQL and the PN
 * section all exclude them — so counting them here persisted an order that every
 * one of those surfaces then treated as unstarted: a form order with no products,
 * rendered in ops as "Medical necessity attested: No".
 *
 * Blank padded rows don't count either, which is why the row predicates are
 * applied rather than just checking array length.
 */
/**
 * The provider's entry for one shell-form category, or undefined if untouched.
 *
 * Typed structurally so both `ShellFormOrderInput[]` and the query-output
 * `ShellFormOrder[]` satisfy it — every caller that needs a single form reaches
 * through this rather than writing its own `.find`.
 */
/**
 * Emptiness for every registered shell form, keyed by category.
 *
 * The Progress Note renderer takes a flag per section, and the shell forms need
 * one entry each. Computed in one place so the five layers that thread it cannot
 * disagree, and so adding a form does not add a prop.
 */
export const shellFormEmptinessByCategory = (
  shellFormOrders: readonly { productCategory: string }[] | null | undefined,
): Record<string, boolean> =>
  Object.fromEntries(
    SHELL_FORM_CATEGORIES.map((category) => [
      category,
      isEmptyShellForm(
        findShellFormOrder(shellFormOrders, category) as
          | ShellFormOrderInput
          | undefined,
      ),
    ]),
  );

/**
 * True when no shell form on the order has content.
 *
 * For the gates that ask "does this order have anything to summarize", which
 * need one boolean. Do not `&&` the map from `shellFormEmptinessByCategory`
 * directly — an object is always truthy, so an order whose only content is a
 * shell form reads as empty and its Progress Note summary is hidden.
 *
 * An empty map is `true`: no registered forms means no shell form content.
 */
export const allShellFormsEmpty = (
  emptinessByCategory: Record<string, boolean>,
): boolean => Object.values(emptinessByCategory).every(Boolean);

export const findShellFormOrder = <T extends { productCategory: string }>(
  shellFormOrders: readonly T[] | null | undefined,
  category: string,
): T | undefined =>
  (shellFormOrders ?? []).find((order) => order.productCategory === category);

export const isEmptyShellForm = (
  shellFormOrder?: ShellFormOrderInput | null,
): boolean => {
  if (!shellFormOrder) {
    return true;
  }
  const {
    lineItems,
    diagnoses,
    isLifetimeLengthOfNeed,
    lengthOfNeedInMonths,
    meetsMedicalNecessity,
  } = shellFormOrder;
  return !(
    (lineItems ?? []).some(isNamedShellFormLineItem) ||
    (diagnoses ?? []).some(isFilledDiagnosisRow) ||
    isLifetimeLengthOfNeed === true ||
    (lengthOfNeedInMonths != null && lengthOfNeedInMonths > 0) ||
    meetsMedicalNecessity === true
  );
};

/**
 * The compressors/admin sets the catalog can name on its own. Mirrors
 * `_CATALOG_COMPRESSORS`/`_CATALOG_ADMINISTRATION_SETS`; supplies-only is excluded since it ships no compressor.
 */
const CATALOG_COMPRESSORS: (NebulizerCompressorType | null | undefined)[] = [
  NebulizerCompressorType.SmallVolume,
  NebulizerCompressorType.LargeVolume,
  NebulizerCompressorType.Ultrasonic,
];

const CATALOG_ADMINISTRATION_SETS: (
  | NebulizerAdministrationSetType
  | null
  | undefined
)[] = [
  NebulizerAdministrationSetType.Mouthpiece,
  NebulizerAdministrationSetType.FaceMask,
  NebulizerAdministrationSetType.TracheostomyMask,
];

/**
 * True when the Nebulizer form's equipment selections yield something to ship.
 *
 * Mirrors `nebulizer_products_present`. An Other selection counts only once the
 * provider has typed what it is -- an empty box beside the radio is a half-
 * answered question, not a device. The medications never count: the form states
 * explicitly that they are not supplied with the order.
 */
export const nebulizerProductsPresent = (
  nebulizerOrder: NebulizerOrderInput,
): boolean =>
  CATALOG_COMPRESSORS.includes(nebulizerOrder.compressorType) ||
  (nebulizerOrder.compressorType === NebulizerCompressorType.Other &&
    !!nebulizerOrder.otherCompressor?.trim()) ||
  CATALOG_ADMINISTRATION_SETS.includes(nebulizerOrder.administrationSetType) ||
  (nebulizerOrder.administrationSetType ===
    NebulizerAdministrationSetType.Other &&
    !!nebulizerOrder.otherAdministrationSet?.trim()) ||
  nebulizerOrder.includesDisposableFilter === true ||
  nebulizerOrder.includesDisposableNebulizerSet === true;

/**
 * The chosen mask's measurements as one phrase, or "" when none were given.
 * One place rather than one per surface: the SWO and Progress Note both print this.
 */
export const nebulizerMaskMeasurements = (nebulizerOrder: {
  administrationSetType?: NebulizerAdministrationSetType | null;
  noseToChinCm?: number | null;
  tracheostomyInnerDiameterMm?: number | null;
  tracheostomyOuterDiameterMm?: number | null;
  tracheostomyLengthMm?: number | null;
}): string => {
  const { administrationSetType } = nebulizerOrder;
  const measurements: [string, number | null | undefined, string][] = [];
  if (administrationSetType === NebulizerAdministrationSetType.FaceMask) {
    measurements.push(["nose to chin", nebulizerOrder.noseToChinCm, "cm"]);
  } else if (
    administrationSetType === NebulizerAdministrationSetType.TracheostomyMask
  ) {
    measurements.push(
      ["inner diameter", nebulizerOrder.tracheostomyInnerDiameterMm, "mm"],
      ["outer diameter", nebulizerOrder.tracheostomyOuterDiameterMm, "mm"],
      ["length", nebulizerOrder.tracheostomyLengthMm, "mm"],
    );
  }
  return measurements
    .filter(([, value]) => value != null)
    .map(([label, value, unit]) => `${label} ${value} ${unit}`)
    .join(", ");
};

type VentilatorMeasurable = {
  interfaceType?: VentilatorInterfaceType | null;
  maskNoseToChinCm?: number | null;
  chestShellWeightLb?: number | null;
  chestShellCircumferenceCm?: number | null;
  tracheostomyInnerDiameterMm?: number | null;
  tracheostomyOuterDiameterMm?: number | null;
  tracheostomyLengthMm?: number | null;
};

/**
 * The fitting measurements each interface needs, with the label and unit to
 * print them under.
 *
 * Mirrors `MEASUREMENTS_BY_INTERFACE` in validate/ventilator.py. One table
 * rather than one per caller: the formatter below and the completeness check
 * beside it must agree on which measurements an interface owns, or the summary
 * reports a fitting as missing while the note prints it.
 */
const VENTILATOR_MEASUREMENTS_BY_INTERFACE: Record<
  VentilatorInterfaceType,
  [keyof VentilatorMeasurable, string, string][]
> = {
  [VentilatorInterfaceType.FaceMask]: [
    ["maskNoseToChinCm", "nose to chin", "cm"],
  ],
  [VentilatorInterfaceType.ChestShell]: [
    ["chestShellWeightLb", "patient weight", "lb"],
    ["chestShellCircumferenceCm", "chest circumference", "cm"],
  ],
  [VentilatorInterfaceType.Tracheostomy]: [
    ["tracheostomyInnerDiameterMm", "inner diameter", "mm"],
    ["tracheostomyOuterDiameterMm", "outer diameter", "mm"],
    ["tracheostomyLengthMm", "length", "mm"],
  ],
};

const ventilatorMeasurementsFor = (
  ventilatorOrder: VentilatorMeasurable,
): [keyof VentilatorMeasurable, string, string][] =>
  ventilatorOrder.interfaceType
    ? VENTILATOR_MEASUREMENTS_BY_INTERFACE[ventilatorOrder.interfaceType]
    : [];

/** The chosen interface's measurements as one line, blanks omitted. */
export const ventilatorInterfaceMeasurements = (
  ventilatorOrder: VentilatorMeasurable,
): string =>
  ventilatorMeasurementsFor(ventilatorOrder)
    .filter(([field]) => ventilatorOrder[field] != null)
    .map(([field, label, unit]) => `${label} ${ventilatorOrder[field]} ${unit}`)
    .join(", ");

/**
 * True when every measurement the chosen interface needs is present.
 *
 * Mirrors `_has_measurement`: 0 is not an answer. It reaches fulfilment as a
 * real size, and no interface ships at 0.
 */
export const ventilatorInterfaceMeasured = (
  ventilatorOrder: VentilatorMeasurable,
): boolean => {
  const required = ventilatorMeasurementsFor(ventilatorOrder);
  return (
    required.length > 0 &&
    required.every(([field]) => {
      const value = ventilatorOrder[field];
      return typeof value === "number" && value > 0;
    })
  );
};

/**
 * The drugs to print under "Medications (not supplied)", in display order.
 *
 * One place rather than one per surface: the OrderSummary, the SWO and the
 * Progress Note summary all print this line, and a drug worded differently
 * between them reads as a different prescription. Mirrors
 * `_plan_of_care_section` in `services/orders/display_model/nebulizer.py`,
 * which is the ops display's copy of the same rule.
 *
 * Structurally typed so both the captured order and the submitted input fit.
 */
export const nebulizerMedicationNames = (nebulizerOrder: {
  medication?: NebulizerMedication | null;
  otherMedication?: string | null;
  usesSterileSalineOrWater?: boolean | null;
}): string[] => {
  const names: string[] = [];
  const { medication, otherMedication } = nebulizerOrder;
  if (medication === NebulizerMedication.Other) {
    // An empty box beside the selected radio names no drug.
    if (otherMedication?.trim()) {
      names.push(otherMedication.trim());
    }
  } else if (medication) {
    names.push(NEBULIZER_MEDICATION_LABELS[medication]);
  }
  if (nebulizerOrder.usesSterileSalineOrWater) {
    names.push("Sterile saline or water");
  }
  return names;
};

/** True when at least one inhalation drug is recorded, Other only once named. */
export const nebulizerMedicationPresent = (
  nebulizerOrder: NebulizerOrderInput,
): boolean =>
  (nebulizerOrder.medication != null &&
    (nebulizerOrder.medication !== NebulizerMedication.Other ||
      !!nebulizerOrder.otherMedication?.trim())) ||
  nebulizerOrder.usesSterileSalineOrWater === true;

/**
 * Every medical-necessity question the interview can ask.
 *
 * Mirrors `_MEDICAL_NECESSITY_FIELDS` in `services/orders/validate/nebulizer.py`.
 * A question missing from this list is one the emptiness check treats as never
 * asked, so a form the provider filled in reads back as untouched.
 */
const NEBULIZER_NECESSITY_FIELDS = [
  "currentlyUsesNebulizer",
  "phIsSecondaryToVenousOrRespiratory",
  "pahIsPrimary",
  "pahHasQualifyingSecondaryCause",
  "pahProgressedDespiteTreatment",
  "pahMeetsPressureThreshold",
  "pahHasSignificantSymptoms",
  "pahCalciumChannelBlockersExhausted",
  "ildConfirmedByHrct",
  "ildMeetsPressureThreshold",
  "ildMeetsWedgePressureThreshold",
  "ildMeetsVascularResistanceThreshold",
  "ildHasSignificantSymptoms",
  "hasDocumentedThickSecretions",
  "alreadyHasCoveredCompressor",
] as const satisfies readonly (keyof NebulizerOrderInput)[];

/**
 * True when the provider has entered nothing clinical on the Nebulizer form.
 *
 * Field-by-field on purpose rather than "is every remaining key at its
 * default": the optional fulfilment details (preferred DME), the length of
 * need, and an unnamed Other selection must NOT count as content. On their own
 * they carry nothing to fulfil, and the backend submit predicate
 * (`nebulizer_form_started`), the sign-ability SQL and the PN section all
 * exclude them -- counting them here would persist an order every one of those
 * surfaces then treats as unstarted.
 */
export const isEmptyNebulizerForm = (
  nebulizerOrder?: NebulizerOrderInput | null,
): boolean => {
  if (!nebulizerOrder) {
    return true;
  }
  return !(
    (nebulizerOrder.nebulizerDiagnoses ?? []).some(isFilledDiagnosisRow) ||
    nebulizerProductsPresent(nebulizerOrder) ||
    // Supplies-only ships no compressor, so it is not a product -- but it is a
    // complete answer to the compressor question, unlike an unnamed Other.
    nebulizerOrder.compressorType === NebulizerCompressorType.SuppliesOnly ||
    nebulizerMedicationPresent(nebulizerOrder) ||
    !!nebulizerOrder.dosingInstructions?.trim() ||
    // Answered, not agreed. A No is engagement with the section just as much as
    // a Yes, and only null means the question was never put -- which is why
    // these are nullable rather than defaulting to false.
    NEBULIZER_NECESSITY_FIELDS.some((field) => nebulizerOrder[field] != null)
  );
};

/**
 * The Ventilator yes/no necessity answers. The narrative is checked separately.
 *
 * Nullable rather than defaulting to false: a No is an answer, and only null
 * means the question was never put.
 */
const VENTILATOR_NECESSITY_FIELDS = [
  "hasQualifyingCondition",
  "isPrimarilyCpapOrBilevel",
] as const satisfies readonly (keyof VentilatorOrderInput)[];

/**
 * True when the Ventilator form's selections yield something to ship.
 *
 * Mirrors `ventilator_products_present`. Either selection alone counts: the form
 * lets a provider pick an interface before a device -- the coupling only greys
 * out illegal options -- and each selection derives its own line item.
 */
export const ventilatorProductsPresent = (
  ventilatorOrder: VentilatorOrderInput,
): boolean =>
  ventilatorOrder.ventilatorType != null ||
  ventilatorOrder.interfaceType != null;

/**
 * True when the length-of-need radio carries a usable answer.
 *
 * Mirrors `_length_of_need_answered`. Lifetime settles it alone; a month count
 * has to be positive, since null is "months chosen but not typed" and a fax or
 * ops path can write 0.
 */
export const ventilatorLengthOfNeedAnswered = (ventilatorOrder: {
  isLifetimeLengthOfNeed?: boolean | null;
  lengthOfNeedInMonths?: number | null;
}): boolean => {
  if (ventilatorOrder.isLifetimeLengthOfNeed === true) {
    return true;
  }
  if (ventilatorOrder.isLifetimeLengthOfNeed === false) {
    const months = ventilatorOrder.lengthOfNeedInMonths;
    return months != null && months > 0;
  }
  return false;
};

/**
 * True when the provider has entered nothing clinical on the Ventilator form.
 *
 * Field-by-field on purpose rather than "is every remaining key at its default":
 * the optional fulfilment details (preferred DME) and the length of need must
 * NOT count as content. On their own they carry nothing to fulfil, and the
 * backend submit predicate (`ventilator_form_started`) excludes them -- counting
 * them here would persist an order that surface then treats as unstarted, and
 * would block an order placed for a different category just because someone
 * clicked Lifetime while looking around.
 */
export const isEmptyVentilatorForm = (
  ventilatorOrder?: VentilatorOrderInput | null,
): boolean => {
  if (!ventilatorOrder) {
    return true;
  }
  return !(
    (ventilatorOrder.ventilatorDiagnoses ?? []).some(isFilledDiagnosisRow) ||
    ventilatorProductsPresent(ventilatorOrder) ||
    !!ventilatorOrder.conditionNarrative?.trim() ||
    VENTILATOR_NECESSITY_FIELDS.some((field) => ventilatorOrder[field] != null)
  );
};

/**
 * The Wound Suction interview answers, by the field each lives on.
 *
 * Read off the served graph on the form itself; listed here because the empty
 * check runs on surfaces that never fetch the graph (the tab indicator, the
 * submit cleanup). A question added to the graph and not added here would leave
 * a form whose only answer is that question looking untouched, so the whole
 * plan-of-care row would never be written. Mirrors `_MEDICAL_NECESSITY_FIELDS`
 * in `services/orders/validate/wound_suction.py`.
 */
const WOUND_SUCTION_NECESSITY_FIELDS = [
  "isContinuingExistingTherapy",
  "therapyStartDate",
  "exceededMaxDressingKits",
  "excessDressingKitsJustification",
  "exceededMaxCanisterSets",
  "hasLargeVolumeDrainageDocumentation",
  "hasNecroticTissueWithEschar",
  "hasUntreatedOsteomyelitis",
  "hasCancerInWound",
  "hasOpenFistula",
  "ulcerType",
  "dressingsAppliedForMoistEnvironment",
  "patientTurnedAndPositioned",
  "usedGroup2Or3SupportSurface",
  "moistureAndIncontinenceManaged",
  "onDiabeticManagementProgram",
  "footPressureReduced",
  "compressionConsistentlyApplied",
  "encouragedToAmbulateAndElevate",
  "isTherapyInitiatedInpatient",
  "npwtIsBestAvailableOption",
  "hasSurgicalOrTraumaticWoundComplication",
  "needsAcceleratedGranulation",
] as const satisfies readonly (keyof WoundSuctionOrderInput)[];

const WOUND_SUCTION_SUPPLY_FIELDS = [
  "includesWoundFiller",
  "includesAdhesiveDrape",
  "includesSuctionPad",
  "includesFoam",
  "includesSorbactDressing",
] as const satisfies readonly (keyof WoundSuctionOrderInput)[];

/**
 * True when a Treatment plan line holds something worth storing.
 *
 * Mirrors `wound_suction_treatment_filled`. The form ships two blank lines, so
 * without this every order would look started the moment the tab is opened.
 */
export const isFilledWoundSuctionTreatment = (
  treatment: WoundSuctionTreatment | WoundSuctionTreatmentInput,
): boolean =>
  (treatment.selectedWoundIndices ?? []).length > 0 ||
  !!treatment.durationOfUse?.trim() ||
  !!treatment.frequencyOfChange?.trim();

/**
 * True when the Wound Suction equipment selections yield something to ship.
 *
 * Mirrors `wound_suction_products_present`. SUPPLIES_ONLY names no pump, so it
 * is deliberately not a product here either -- it is how a refill of an existing
 * therapy is ordered, and the supplies below it are what ships.
 */
export const woundSuctionProductsPresent = (
  order: WoundSuctionOrderInput,
): boolean =>
  (order.machineType != null &&
    order.machineType !== WoundSuctionMachineType.SuppliesOnly) ||
  (order.canisterType != null &&
    (order.canisterType !== WoundSuctionCanisterType.Other ||
      !!order.otherCanister?.trim())) ||
  WOUND_SUCTION_SUPPLY_FIELDS.some((field) => order[field] === true);

/**
 * True when nothing clinical has been entered on the Wound Suction form.
 *
 * The inverse of `wound_suction_form_started`, so the two must not drift. The
 * wounds are deliberately not counted: they ride on the shared `woundOrder`
 * slice, so counting them would light up this tab for a Wound Care order.
 */
export const isEmptyWoundSuctionForm = (
  woundSuctionOrder?: WoundSuctionOrderInput | null,
): boolean => {
  if (!woundSuctionOrder) {
    return true;
  }
  return !(
    woundSuctionProductsPresent(woundSuctionOrder) ||
    // Separately from productsPresent, because SUPPLIES_ONLY yields no product.
    woundSuctionOrder.machineType != null ||
    (woundSuctionOrder.woundSuctionTreatments ?? []).some(
      isFilledWoundSuctionTreatment,
    ) ||
    // Answered, not agreed: a No is engagement with the section just as much as
    // a Yes, and only null means the question was never put.
    WOUND_SUCTION_NECESSITY_FIELDS.some(
      (field) => woundSuctionOrder[field] != null,
    )
  );
};

/**
 * A line item counts as a product only if it names one (size/laterality don't
 * stand in for one). Mirrors `_has_named_product` in the backend validator, so
 * every TS surface must judge rows through this rather than an inline check.
 */
export const isNamedOffTheShelfOrthoticsLineItem = (lineItem: {
  productName?: string | null;
}): boolean => !!lineItem.productName?.trim();

/**
 * True when nothing clinical has been entered on the Off-the-Shelf Orthotics
 * form — the inverse of the backend's `off_the_shelf_orthotics_form_started`, so
 * the two must not drift. Checks fields explicitly (blank padded rows don't count).
 */
export const isEmptyOffTheShelfOrthoticsForm = (
  offTheShelfOrthoticsOrder?: OffTheShelfOrthoticsOrderInput | null,
): boolean => {
  if (!offTheShelfOrthoticsOrder) {
    return true;
  }
  const {
    offTheShelfOrthoticsLineItems,
    offTheShelfOrthoticsDiagnoses,
    isLifetimeLengthOfNeed,
    lengthOfNeedInMonths,
  } = offTheShelfOrthoticsOrder;
  return !(
    (offTheShelfOrthoticsLineItems ?? []).some(
      isNamedOffTheShelfOrthoticsLineItem,
    ) ||
    (offTheShelfOrthoticsDiagnoses ?? []).some(isFilledDiagnosisRow) ||
    isLifetimeLengthOfNeed === true ||
    (lengthOfNeedInMonths != null && lengthOfNeedInMonths > 0)
  );
};

// Mirrors `_MEDICAL_NECESSITY_FIELDS` in validate/custom_orthotics.py. A
// question missing here reads back as never asked.
const CUSTOM_ORTHOTICS_NECESSITY_FIELDS = [
  "documentsWhyPrefabricatedInsufficient",
  "isAmbulatory",
  "spinalTreatmentGoals",
  "hasRecentKneeInjuryOrSurgery",
  "hasLigamentousInstability",
  "hasOaPainOrMobilityLoss",
  "orthosisProvidesVarusValgusAdjustment",
  "isWillingToUseKneeOrthosis",
  "orthosisBenefitsFunctionality",
  "requiresAnkleFootStabilization",
  "requiresKneeStabilization",
  "afoCustomFabricationCriteria",
  "depthShoeCannotAccommodateDeformity",
] as const satisfies readonly (keyof CustomOrthoticsOrderInput)[];

// Mirrors `CUSTOM_ORTHOTICS_PLAN_OF_CARE_FIELDS` in
// validate/custom_orthotics.py. A field missing here is one the provider types
// and the submit scrub discards, because it nulls the whole slice.
const CUSTOM_ORTHOTICS_PLAN_OF_CARE_FIELDS = [
  "headNeckSpineSpecifications",
  "upperLimbSpecifications",
  "lowerLimbSpecifications",
  "kneeBracesSpecifications",
  "feetSpecifications",
  "patientWeightLbs",
  "patientAgeYears",
  "shoeSize",
  "shoeWidth",
] as const satisfies readonly (keyof CustomOrthoticsOrderInput)[];

/**
 * True when a Custom Orthotics row carries detail but names nothing to fulfil.
 *
 * Mirrors `_is_partial_item` in validate/custom_orthotics.py. The insert
 * filter drops such a row, so a surface that prints around it shows an order
 * the provider did not place.
 *
 * Relies on the form leaving the quantity tri-state at null for the blank
 * padded rows it submits for unused slots. A false there means a number was
 * named, which is a half-answered row.
 */
export const isCustomOrthoticsItemPartial = (item: {
  region: string;
  productName?: string | null;
  bodyParts?: readonly string[] | null;
  laterality?: string | null;
  jointType?: string | null;
  quantity?: number | null;
  isMaximumAllowedQuantity?: boolean | null;
}): boolean => {
  if (isOrderedCustomOrthoticsItem(item)) {
    return false;
  }
  return !!(
    item.laterality ||
    item.jointType ||
    item.quantity != null ||
    item.isMaximumAllowedQuantity != null ||
    item.bodyParts?.length
  );
};

/**
 * The inverse of the backend's `custom_orthotics_form_started`, so the two must
 * not drift. The specification notes and the fitting measurements count, because
 * that predicate counts them: a lone note starts the form, and the submit gate
 * then asks for a diagnosis, an item and a length of need. A visible error beats
 * scrubbing the text away.
 */
export const isEmptyCustomOrthoticsForm = (
  customOrthoticsOrder?: CustomOrthoticsOrderInput | null,
): boolean => {
  if (!customOrthoticsOrder) {
    return true;
  }
  const {
    customOrthoticsLineItems,
    customOrthoticsDiagnoses,
    isLifetimeLengthOfNeed,
    lengthOfNeedInMonths,
  } = customOrthoticsOrder;
  return !(
    (customOrthoticsLineItems ?? []).some(isOrderedCustomOrthoticsItem) ||
    (customOrthoticsDiagnoses ?? []).some(isFilledDiagnosisRow) ||
    isLifetimeLengthOfNeed === true ||
    (lengthOfNeedInMonths != null && lengthOfNeedInMonths > 0) ||
    // Answered, not agreed: only null means the question was never put. An
    // empty multi-select is unanswered; ["NONE"] is a real answer.
    CUSTOM_ORTHOTICS_NECESSITY_FIELDS.some((field) => {
      const value = customOrthoticsOrder[field];
      return Array.isArray(value) ? value.length > 0 : value != null;
    }) ||
    // A note counts only when it holds more than whitespace. A measurement
    // counts whenever it is set, matching `_has_plan_of_care_detail`.
    CUSTOM_ORTHOTICS_PLAN_OF_CARE_FIELDS.some((field) => {
      const value = customOrthoticsOrder[field];
      return typeof value === "string" ? value.trim() !== "" : value != null;
    })
  );
};

/**
 * A row counts as a product only if it names one. Mirrors `_has_named_product`
 * in the backend validator.
 */
export const isNamedProstheticsLineItem = (lineItem: {
  productName?: string | null;
}): boolean => !!lineItem.productName?.trim();

// Mirrors `_PLAN_OF_CARE_FIELDS` in validate/prosthetics.py. A field missing
// here is one the provider types and the submit scrub then discards.
const PROSTHETICS_PLAN_OF_CARE_FIELDS = [
  "functionalLevel",
  "mastectomyAmputationSite",
  "mastectomyBandSize",
  "mastectomyCupSize",
  "lowerLimbRightAmputationSite",
  "lowerLimbRightCircumference",
  "lowerLimbRightAttachmentMechanism",
  "lowerLimbLeftAmputationSite",
  "lowerLimbLeftCircumference",
  "lowerLimbLeftAttachmentMechanism",
  "toeFillersWeight",
  "toeFillersAge",
  "toeFillersShoeSize",
  "toeFillersShoeWidth",
  "toeFillersLeftToesMissing",
  "toeFillersRightToesMissing",
] as const satisfies readonly (keyof ProstheticsOrderInput)[];

/**
 * The inverse of the backend's `prosthetics_form_started`, so the two must not
 * drift. `preferredDmeCompany` is coordination-only and excluded.
 */
export const isEmptyProstheticsForm = (
  prostheticsOrder?: ProstheticsOrderInput | null,
): boolean => {
  if (!prostheticsOrder) {
    return true;
  }
  const {
    prostheticsLineItems,
    prostheticsDiagnoses,
    isLifetimeLengthOfNeed,
    lengthOfNeedInMonths,
  } = prostheticsOrder;
  return !(
    (prostheticsLineItems ?? []).some(isNamedProstheticsLineItem) ||
    (prostheticsDiagnoses ?? []).some(isFilledDiagnosisRow) ||
    isLifetimeLengthOfNeed === true ||
    (lengthOfNeedInMonths != null && lengthOfNeedInMonths > 0) ||
    // A measurement counts whenever it is set.
    PROSTHETICS_PLAN_OF_CARE_FIELDS.some((field) => {
      const value = prostheticsOrder[field];
      return typeof value === "string" ? value.trim() !== "" : value != null;
    })
  );
};

/**
 * True when a device selection yields a product to fulfil. Mirrors
 * `wheelchairs_and_scooters_products_present`; CUSHION_ONLY counts too -- it's billable, just not a wheelchair.
 */
export const wheelchairsAndScootersProductsPresent = (
  order: WheelchairsAndScootersOrderInput,
): boolean => order.deviceType != null;

/**
 * Whether a line item is the device's own row, not an add-on. An "Other" device
 * has no catalog row, so it matches by a missing formItemId; add-ons always have one.
 */
export const isWheelchairsAndScootersDeviceLineItem = (
  deviceType: WheelchairsAndScootersDeviceType | null | undefined,
  formItemId: number | null | undefined,
  productName: string | null | undefined,
): boolean => {
  if (!deviceType) return false;
  if (WHEELCHAIRS_AND_SCOOTERS_OTHER_DEVICE_TYPES.has(deviceType)) {
    return formItemId == null;
  }
  return productName === WHEELCHAIRS_AND_SCOOTERS_DEVICE_ITEM_NAMES[deviceType];
};

/**
 * Order Items -> Specifications. Optional and carry no coverage criterion --
 * mirrors `_SPEC_FIELDS` in `validate/wheelchairs_and_scooters.py`.
 */
const WHEELCHAIRS_AND_SCOOTERS_SPEC_FIELDS = [
  "wheelchairHeight",
  "seatDepth",
  "wheelchairSeatWidth",
  "scooterSeatWidth",
  "scooterWheels",
  "scooterSwivelSeat",
  "scooterSteeringHandle",
] as const satisfies readonly (keyof WheelchairsAndScootersOrderInput)[];

/**
 * True when nothing clinical is entered on the Wheelchairs & Scooters form.
 * Fulfilment details (discharge date, DME, delivery) don't count, mirroring `isEmptyNebulizerForm`/the backend's `wheelchairs_and_scooters_form_started`.
 */
export const isEmptyWheelchairsAndScootersForm = (
  order?: WheelchairsAndScootersOrderInput | null,
): boolean => {
  if (!order) {
    return true;
  }
  return !(
    (order.wheelchairsAndScootersDiagnoses ?? []).some(isFilledDiagnosisRow) ||
    wheelchairsAndScootersProductsPresent(order) ||
    order.patientHeightFeet != null ||
    order.patientHeightInches != null ||
    order.patientWeightLbs != null ||
    order.isLifetimeLengthOfNeed != null ||
    !!order.otherDeviceDescription?.trim() ||
    (order.wheelchairsAndScootersLineItems ?? []).length > 0 ||
    WHEELCHAIRS_AND_SCOOTERS_SPEC_FIELDS.some((field) => order[field] != null)
  );
};

/**
 * The names the backend derives from the system selections, so a row carrying
 * one is not a provider pick. Mirrors `DERIVED_ITEM_NAMES`.
 */
export const DIABETES_DERIVED_ITEM_NAMES: ReadonlySet<string> = new Set([
  "True Metrix Pro BGM System",
  "Roche Accu-Chek Guide",
  "OneTouch Verio Flex",
  "FreeStyle Lite BGM System",
  "Dexcom G7 System",
  "FreeStyle Libre 3 Plus System",
  "Dexcom G7 Sensors",
  "FreeStyle Libre 3 Plus Sensors",
  "Beta Bionics iLet",
  "Medtronic MiniMed",
  "Tandem Mobi",
  "Tandem t:slim X2 insulin pump",
]);

/** True when a row names a product. Mirrors `is_named_diabetes_line_item`. */
export const isNamedDiabetesLineItem = (item: {
  productName?: string | null;
}): boolean => !!item.productName?.trim();

/** True when a row is one the backend derives. Mirrors `is_derived_line_item`. */
export const isDerivedDiabetesLineItem = (item: {
  productName?: string | null;
}): boolean => DIABETES_DERIVED_ITEM_NAMES.has(item.productName ?? "");

/**
 * True when the order yields something to fulfil: a system, or a row the
 * provider picked, but never an echoed-back derived row. Mirrors the product
 * half of `diabetes_form_started`, not `diabetes_products_present`: the
 * backend drops a meter the patient already owns, and this must not.
 */
export const diabetesProductsPresent = (order: DiabetesOrderInput): boolean =>
  order.bgmSystem != null ||
  order.cgmSystem != null ||
  order.pumpSystem != null ||
  (order.diabetesLineItems ?? []).some(
    (item) => isNamedDiabetesLineItem(item) && !isDerivedDiabetesLineItem(item),
  );

/**
 * Named explicitly rather than derived from the input's remaining keys, so
 * adding a field cannot silently change what "started" means.
 */
const DIABETES_NECESSITY_FIELDS = [
  "currentlyOnCgmTherapy",
  "treatedWithInsulin",
  "insulinTreatmentType",
  "mdiInjectionsPerDay",
  "smbgTestsPerDay",
  "cPeptideNgMl",
  "cPeptideLabLowerLimitNgMl",
  "fastingBloodSugarMgDl",
  "hasRenalInsufficiency",
  "positiveBetaCellAutoantibody",
  "completedDiabetesEducationProgram",
  "onMdiWithSelfAdjustments6Months",
  "glucoseTestsPerDayPrior2Months",
  "cgmDocumentationOnFile",
  "usedPumpBeforeMedicare",
  "glucoseTestsPerDayBeforeMedicare",
] as const satisfies readonly (keyof DiabetesOrderInput)[];

/**
 * True when nothing clinical is entered. The preferred DME company does not
 * count, or visiting the tab would drag the order into this form's validation.
 */
export const isEmptyDiabetesForm = (
  order?: DiabetesOrderInput | null,
): boolean => {
  if (!order) {
    return true;
  }
  return !(
    (order.diabetesDiagnoses ?? []).some(isFilledDiagnosisRow) ||
    diabetesProductsPresent(order) ||
    order.bgmIsInitialOrder != null ||
    order.cgmIsInitialOrder != null ||
    order.pumpIsInitialOrder != null ||
    order.cgmSensorType != null ||
    order.isLifetimeLengthOfNeed != null ||
    order.lengthOfNeedInMonths != null ||
    (order.hypoglycemicEventHistory ?? []).length > 0 ||
    (order.mdiQualifyingConditions ?? []).length > 0 ||
    DIABETES_NECESSITY_FIELDS.some((field) => order[field] != null)
  );
};

export const isCompressionPlanOfCareEmpty = (
  compressionOrder: InputMaybe<CompressionOrderInput>,
) => {
  if (!compressionOrder || !compressionOrder.compressionPlanOfCare) {
    return true;
  }
  const { ...keys } = compressionOrder.compressionPlanOfCare;
  if (Object.values(keys).every((val) => val === "" || val === null)) {
    return true;
  }
  return false;
};

// Processors:
const sanitizeAndReorderWoundOrder = (orderState: ProviderReferralInput) => {
  const keptWounds: WoundInput[] = [];

  const shouldKeepWound = (
    wound: WoundInput,
    index: number,
    woundIndices: number[],
  ): boolean => {
    if (woundIndices.includes(index)) {
      return true;
    }

    return !isWoundEmpty(wound);
  };

  const woundIndices: number[] =
    orderState?.woundOrder?.woundCareLineItems?.flatMap(
      (item) => item.woundIndicesUsed || [],
    ) ?? [];

  const keptWoundIndices: number[] =
    orderState?.woundOrder?.wounds.reduce((indices: number[], wound, index) => {
      if (shouldKeepWound(wound, index, woundIndices)) {
        keptWounds.push(wound);
        indices.push(index);
      }
      return indices;
    }, []) ?? [];

  const oldWoundIndexToNew = Object.fromEntries(
    keptWoundIndices.map((oldIndex, newIndex) => [oldIndex, newIndex]),
  );

  const remappedWoundCareLineItems =
    orderState.woundOrder?.woundCareLineItems.map((lineItem) => ({
      ...lineItem,
      woundIndicesUsed: (lineItem.woundIndicesUsed || [])
        .map((oldIndex) => oldWoundIndexToNew[oldIndex])
        .filter((newIndex) => newIndex != null),
    })) ?? [];

  return {
    wounds: keptWounds,
    woundCareLineItems: remappedWoundCareLineItems,
    // Returned rather than kept private: wound-care line items are no longer the
    // only thing holding wound positions. Every other holder has to be remapped
    // through the SAME map, or it ends up pointing at a different wound than the
    // one the provider picked.
    oldWoundIndexToNew,
  };
};

export function updateMedicareOrderPreferences(
  orderState: ProviderReferralInput,
) {
  orderState.woundOrder?.woundCareLineItems?.forEach((lineItem) => {
    lineItem.selectedWoundsPreferenceIds = Array(
      lineItem.woundIndicesUsed?.length,
    ).fill(DEFAULT_MEDICARE_WOUND_PREFERENCE_ID);
  });
}

/**
 * Removes wound line items whose formItemId matches one of the additional
 * products. Prevents the same ADDITIONAL-subcategory item from being submitted
 * twice when a stale entry survives a reorder + autofill clear, or when a user
 * pins a product into a wound row AND checks it under Additional Products
 * (VM-8752). Items without a formItemId (custom rows) are kept as-is.
 */
export const dropLineItemsMatchingAdditionalProducts = (
  woundCareLineItems: WoundCareLineItemInput[],
  woundAdditionalProducts: WoundCareLineItemInput[],
): WoundCareLineItemInput[] => {
  const additionalProductFormItemIds = new Set(
    woundAdditionalProducts
      .map((item) => item.formItemId)
      .filter((id): id is number => id != null),
  );
  return woundCareLineItems.filter(
    (item) =>
      item.formItemId == null ||
      !additionalProductFormItemIds.has(item.formItemId),
  );
};

export const prepareOrderState = (
  orderState: ProviderReferralInput,
  planOfCares: PlanOfCareInput[],
  diagnoses: DiagnosisInput[],
  modernLineItems: ModernLineItemInput[],
  uploadProgressNoteNow: boolean,
  isMedicareBlockingEligible: boolean,
  isHighCostFlaggingEligible: boolean,
  selectedCompressionItems: CompressionLineItemInput[],
  standardItems: FormOptionItem[],
  urologyStandardItems: FormOptionItem[],
  ostomyStandardItems: FormOptionItem[],
  cpapStandardItems: FormOptionItem[],
  woundAdditionalProducts: WoundCareLineItemInput[],
  departmentId: number,
  categoryDiagnosisTypeIdToDiagnosisTypeMap: Record<number, DiagnosisType>,
  updates: Partial<ProviderReferralInput>,
) => {
  orderState = JSON.parse(JSON.stringify(orderState));

  // Filled in by the wound sanitizer below, and read by every other slice that
  // holds wound positions. Empty when the order has no wounds at all, in which
  // case nothing needed remapping.
  let woundIndexRemap: Record<number, number> = {};

  Object.assign(orderState, updates);

  // Checking Self Pay only hides the insurance UI — payor data typed before
  // the switch lingers in form state and would submit alongside selfPay.
  if (orderState.selfPay) {
    orderState.insuranceInfo = initialInsuranceInfo();
    orderState.workersCompInfo = initialWorkersCompInfo();
    orderState.autoInsuranceInfo = initialAutoInsuranceInfo();
  }

  orderState["departmentId"] = departmentId;
  orderState.planOfCareInputs = planOfCares;
  orderState.diagnosisInputs = sanitizeDiagnosisInputs(
    diagnoses,
    categoryDiagnosisTypeIdToDiagnosisTypeMap,
  );
  orderState.modernLineItemInputs = modernLineItems;
  orderState.uploadProgressNoteNow = uploadProgressNoteNow;

  orderState.isMedicareBlockingEligible = isMedicareBlockingEligible;
  orderState.isHighCostFlaggingEligible = isHighCostFlaggingEligible;

  if (orderState.woundOrder) {
    const { wounds, woundCareLineItems, oldWoundIndexToNew } =
      sanitizeAndReorderWoundOrder(orderState);
    woundIndexRemap = oldWoundIndexToNew;
    orderState.woundOrder.wounds = wounds;
    orderState.woundOrder.woundCareLineItems = woundCareLineItems;

    const newWoundCareLineItems = filterRecommendedSupplies(
      Category.WoundCare,
      initialWoundItem({ productName: "", formItemId: null }),
      orderState.woundOrder.woundCareLineItems,
      standardItems,
    );

    if (isMedicareBlockingEligible) {
      updateMedicareOrderPreferences(orderState);
    }

    orderState.woundOrder.woundCareLineItems = [
      ...dropLineItemsMatchingAdditionalProducts(
        newWoundCareLineItems,
        woundAdditionalProducts,
      ),
      ...woundAdditionalProducts,
    ];
  }

  const checkedCompressionLineItems: CompressionLineItemInput[] = Object.values(
    selectedCompressionItems || {},
  )
    .flatMap((items) => items)
    .filter((item) => !!item.productName);
  if (
    !checkedCompressionLineItems.length &&
    !isCompressionPlanOfCareEmpty(orderState.compressionOrder!)
  ) {
    orderState.compressionOrder!.compressionLineItems = [];
  } else {
    orderState.compressionOrder!.compressionLineItems =
      checkedCompressionLineItems;
  }

  if (orderState.urologyOrder) {
    orderState.urologyOrder.urologyLineItems = filterRecommendedSupplies(
      Category.Urology,
      initialUrologyItem("", null, []),
      orderState.urologyOrder.urologyLineItems,
      urologyStandardItems,
    );
    if (isEmptyUrologyForm(orderState.urologyOrder)) {
      orderState.urologyOrder.startDate = null;
      orderState.urologyOrder.urologyLineItems = [];
    }
  }

  if (orderState.ostomyOrder) {
    orderState.ostomyOrder.ostomyLineItems = filterRecommendedSupplies(
      Category.Ostomy,
      initialOstomyItem("", null, []),
      orderState.ostomyOrder.ostomyLineItems,
      ostomyStandardItems,
    );
    if (isEmptyOstomyForm(orderState.ostomyOrder)) {
      orderState.ostomyOrder.startDate = null;
      orderState.ostomyOrder.ostomyLineItems = [];
    }
  }

  if (orderState.cpapOrder) {
    const cpapItemByName = (name: string) => {
      const item = cpapStandardItems.find((i) => i.name === name);
      return item ? { formItemId: item.id, productName: item.name } : null;
    };
    const cpapMachineFormItemMap: Record<string, string> = {
      cpap: "CPAP",
      auto_cpap: "Auto CPAP",
      bilevel: "Bi-Level PAP",
      auto_bilevel: "Auto Bi-Level PAP",
      bipap_st: "BiPAP ST",
    };
    const cpapHumidifierFormItemMap: Record<string, string> = {
      heated: "Heated Humidifier",
      non_heated: "Non-heated Humidifier",
    };
    const cpapMaskFormItemMap: Record<string, string> = {
      full_face: "Full Face",
      nasal: "Nasal Mask",
      nasal_pillow: "Nasal Pillow",
    };
    const machineName = orderState.cpapOrder.machineType
      ? cpapMachineFormItemMap[orderState.cpapOrder.machineType]
      : null;
    const machineLineItem = machineName ? cpapItemByName(machineName) : null;
    const humidifierName = orderState.cpapOrder.humidifierType
      ? cpapHumidifierFormItemMap[orderState.cpapOrder.humidifierType]
      : null;
    const humidifierLineItem = humidifierName
      ? cpapItemByName(humidifierName)
      : null;
    const maskName = orderState.cpapOrder.maskType
      ? cpapMaskFormItemMap[orderState.cpapOrder.maskType]
      : null;
    const maskLineItem = maskName ? cpapItemByName(maskName) : null;
    const autoLineItemIds = new Set(
      [
        machineLineItem?.formItemId,
        humidifierLineItem?.formItemId,
        maskLineItem?.formItemId,
      ].filter((formItemId) => formItemId != null),
    );
    const existingLineItems = (orderState.cpapOrder.cpapLineItems || []).filter(
      (lineItem) =>
        !!lineItem.productName &&
        (lineItem.formItemId == null ||
          !autoLineItemIds.has(lineItem.formItemId)),
    );
    orderState.cpapOrder.cpapLineItems = [
      ...(machineLineItem ? [machineLineItem] : []),
      ...(humidifierLineItem ? [humidifierLineItem] : []),
      ...(maskLineItem ? [maskLineItem] : []),
      ...existingLineItems,
    ];

    // Safety net: clear pressure fields not relevant to the selected machine type.
    // When adding a new machine-specific field to CpapOrderInput, add it to the
    // appropriate block below so it gets cleared when switching machine types.
    const machineType = orderState.cpapOrder.machineType;
    if (machineType !== "match_existing")
      orderState.cpapOrder.makeAndModel = null;
    if (machineType !== "cpap") orderState.cpapOrder.cpapPressure = null;
    if (machineType !== "auto_cpap") {
      orderState.cpapOrder.autoCpapPressureMin = null;
      orderState.cpapOrder.autoCpapPressureMax = null;
      orderState.cpapOrder.autoCpapExpiratoryRelief = null;
      orderState.cpapOrder.autoCpapRampTime = null;
      orderState.cpapOrder.autoCpapStartingPressure = null;
    }
    if (machineType !== "bilevel") {
      orderState.cpapOrder.bilevelIpap = null;
      orderState.cpapOrder.bilevelEpap = null;
    }
    if (machineType !== "auto_bilevel") {
      orderState.cpapOrder.autoBilevelPressureSupport = null;
      orderState.cpapOrder.autoBilevelMaxIpap = null;
      orderState.cpapOrder.autoBilevelMinEpap = null;
    }
    if (machineType !== "bipap_st") {
      orderState.cpapOrder.bipapStMaxIpap = null;
      orderState.cpapOrder.bipapStMinEpap = null;
      orderState.cpapOrder.bipapStPressureSupportMin = null;
      orderState.cpapOrder.bipapStPressureSupportMax = null;
      orderState.cpapOrder.bipapStBackupRate = null;
      orderState.cpapOrder.bipapStRampTime = null;
    }
    if (!orderState.cpapOrder.usesOxygen) {
      orderState.cpapOrder.nocturnalOxygen = null;
    }

    const maskType = orderState.cpapOrder.maskType;
    if (maskType !== "match_existing") {
      orderState.cpapOrder.existingSizeAndModel = null;
    }
    if (maskType !== "full_face") {
      orderState.cpapOrder.noseToChin = null;
    }
    if (maskType !== "nasal" && maskType !== "nasal_pillow") {
      orderState.cpapOrder.noseWidth = null;
      orderState.cpapOrder.noseLength = null;
    }

    if (orderState.cpapOrder.lengthOfNeedType === "lifetime") {
      orderState.cpapOrder.lengthOfNeedMonths = 99;
    } else if (!orderState.cpapOrder.lengthOfNeedType) {
      orderState.cpapOrder.lengthOfNeedMonths = null;
    }

    // Clear medical necessity conditional fields
    if (orderState.cpapOrder.hadCpapLast5Years !== true) {
      orderState.cpapOrder.priorCpapReason = null;
    }
    if (orderState.cpapOrder.ahiRdiQualifyingOption !== "5_to_14_events") {
      orderState.cpapOrder.ahiRdiSymptoms = null;
    }
    if (orderState.cpapOrder.hadBilevelLast5Years !== true) {
      orderState.cpapOrder.priorBilevelReason = null;
    }
    if (orderState.cpapOrder.bilevelQualifyingStatement !== "no_sleep_apnea") {
      orderState.cpapOrder.noSleepApneaEvidence = null;
    }
    const bilevelTests = orderState.cpapOrder.bilevelTestsPerformed || [];
    if (!bilevelTests.includes("sleep_oximetry")) {
      orderState.cpapOrder.bilevelOximetryConfirmed = null;
    }
    if (!bilevelTests.includes("arterial_blood_gas")) {
      orderState.cpapOrder.bilevelBloodGasConfirmed = null;
    }

    if (orderState.cpapOrder.cpapDiagnoses) {
      orderState.cpapOrder.cpapDiagnoses = orderState.cpapOrder.cpapDiagnoses
        .filter((d) => d.icd10Code || d.diagnosis)
        .map(({ icd10Code, diagnosis }) => ({ icd10Code, diagnosis }));
    }

    if (isEmptyCpapForm(orderState.cpapOrder)) {
      orderState.cpapOrder = null;
    }
  }

  if (orderState.shellFormOrders?.length) {
    // Shell forms render blank padded rows and write the padded array back to the
    // store on any edit, so drop empty rows before they can count as content.
    // Then drop whole forms the provider only visited: an entry exists as soon as
    // the store is touched, and submitting it would persist a form order with
    // nothing to fulfil.
    orderState.shellFormOrders = orderState.shellFormOrders
      .map((shellFormOrder) => ({
        ...shellFormOrder,
        diagnoses: (shellFormOrder.diagnoses ?? []).filter(
          isFilledDiagnosisRow,
        ),
        lineItems: (shellFormOrder.lineItems ?? [])
          .filter(isNamedShellFormLineItem)
          // A product row is an order for at least one unit. The form already
          // defaults to 1; this is the backstop for rows round-tripped through an
          // edit, so a persisted quantity always matches the displayed one.
          .map((lineItem) => ({
            ...lineItem,
            quantity: lineItem.quantity ?? 1,
          })),
      }))
      .filter((shellFormOrder) => !isEmptyShellForm(shellFormOrder));
  }

  if (orderState.offTheShelfOrthoticsOrder) {
    const orthotics = orderState.offTheShelfOrthoticsOrder;
    // The form pads its rows with blanks; drop them before they count as content
    // downstream.
    orthotics.offTheShelfOrthoticsDiagnoses = (
      orthotics.offTheShelfOrthoticsDiagnoses ?? []
    ).filter(isFilledDiagnosisRow);
    orthotics.offTheShelfOrthoticsLineItems = (
      orthotics.offTheShelfOrthoticsLineItems ?? []
    ).filter(isNamedOffTheShelfOrthoticsLineItem);

    if (isEmptyOffTheShelfOrthoticsForm(orthotics)) {
      orderState.offTheShelfOrthoticsOrder = null;
    }
  }

  if (orderState.customOrthoticsOrder) {
    const customOrthotics = orderState.customOrthoticsOrder;
    // The form pads its diagnosis rows with blanks; drop them before they count
    // as content downstream, the same as the two forms above.
    //
    // The line items are deliberately left alone. The backend reports a row that
    // carries detail but names nothing to fulfil as "item row N is incomplete",
    // so a filter here would delete the provider's half-entered row instead.
    customOrthotics.customOrthoticsDiagnoses = (
      customOrthotics.customOrthoticsDiagnoses ?? []
    ).filter(isFilledDiagnosisRow);

    if (isEmptyCustomOrthoticsForm(customOrthotics)) {
      orderState.customOrthoticsOrder = null;
    }
  }

  if (orderState.prostheticsOrder) {
    const prosthetics = orderState.prostheticsOrder;
    // The form pads its rows with blanks; drop them before they count.
    prosthetics.prostheticsDiagnoses = (
      prosthetics.prostheticsDiagnoses ?? []
    ).filter(isFilledDiagnosisRow);
    prosthetics.prostheticsLineItems = (
      prosthetics.prostheticsLineItems ?? []
    ).filter(isNamedProstheticsLineItem);

    if (isEmptyProstheticsForm(prosthetics)) {
      orderState.prostheticsOrder = null;
    }
  }

  if (orderState.ventilatorOrder) {
    const ventilator = orderState.ventilatorOrder;
    // The diagnosis table renders a blank row and writes it back to the store on
    // any edit, so drop it before it counts as content.
    ventilator.ventilatorDiagnoses = (
      ventilator.ventilatorDiagnoses ?? []
    ).filter(isFilledDiagnosisRow);

    if (isEmptyVentilatorForm(ventilator)) {
      orderState.ventilatorOrder = null;
    }
  }
  if (orderState.nebulizerOrder) {
    const nebulizer = orderState.nebulizerOrder;
    // The diagnosis table renders blank padded rows and writes the padded array
    // back to the store on any edit, so drop them before they count as content.
    // There is no line-item equivalent: those rows are derived server-side from
    // the equipment selections, so the input type carries none.
    nebulizer.nebulizerDiagnoses = (nebulizer.nebulizerDiagnoses ?? []).filter(
      isFilledDiagnosisRow,
    );

    if (isEmptyNebulizerForm(nebulizer)) {
      orderState.nebulizerOrder = null;
    }
  }

  if (orderState.woundSuctionOrder) {
    const woundSuction = orderState.woundSuctionOrder;
    // The Treatment plan table renders blank lines and writes them back to the
    // store on any edit, so drop them before they count as content. The line
    // items have no equivalent: they are derived server-side from the equipment
    // selections, so the input type carries none.
    //
    // The wound positions go through the same remap the wound-care line items
    // do. The wounds table renders blank padded rows and the sanitizer above
    // drops them, so position 2 before that pass is not position 2 after it --
    // and the backend resolves these against the rows that actually got
    // inserted. A position whose wound was dropped falls out here rather than
    // resolving to whichever wound moved into that slot.
    woundSuction.woundSuctionTreatments = (
      woundSuction.woundSuctionTreatments ?? []
    )
      .map((treatment) => ({
        ...treatment,
        selectedWoundIndices: (treatment.selectedWoundIndices ?? [])
          .map((oldIndex) => woundIndexRemap[oldIndex])
          .filter((newIndex) => newIndex != null),
      }))
      .filter(isFilledWoundSuctionTreatment);

    if (isEmptyWoundSuctionForm(woundSuction)) {
      orderState.woundSuctionOrder = null;
    }
  }

  if (orderState.wheelchairsAndScootersOrder) {
    const wheelchairsAndScooters = orderState.wheelchairsAndScootersOrder;
    // Blank padded diagnosis rows must not count as content (same as
    // Nebulizer above); add-on line items are always fully populated already.
    wheelchairsAndScooters.wheelchairsAndScootersDiagnoses = (
      wheelchairsAndScooters.wheelchairsAndScootersDiagnoses ?? []
    ).filter(isFilledDiagnosisRow);

    if (isEmptyWheelchairsAndScootersForm(wheelchairsAndScooters)) {
      orderState.wheelchairsAndScootersOrder = null;
    }
  }

  if (orderState.diabetesOrder) {
    const diabetes = orderState.diabetesOrder;
    diabetes.diabetesDiagnoses = (diabetes.diabetesDiagnoses ?? []).filter(
      isFilledDiagnosisRow,
    );
    diabetes.diabetesLineItems = (diabetes.diabetesLineItems ?? [])
      .filter(isNamedDiabetesLineItem)
      .map((item) => ({ ...item, quantity: item.quantity ?? 1 }));

    if (isEmptyDiabetesForm(diabetes)) {
      orderState.diabetesOrder = null;
    }
  }

  if (orderState.miscellaneousOrder) {
    orderState.miscellaneousOrder.diagnoses =
      orderState.miscellaneousOrder.diagnoses.filter(
        (diagnosis) => diagnosis.diagnosis !== "",
      );
    orderState.miscellaneousOrder.lineItems =
      orderState.miscellaneousOrder.lineItems.filter(
        (lineItem) =>
          lineItem.productName !== "" ||
          lineItem.quantity !== 0 ||
          lineItem.remarks !== "" ||
          lineItem.sizeSpecification !== "",
      );
  }

  delete (orderState as any).signerInfo;
  delete (orderState as any).clinicInfo;

  // TODO(shirlyn): temporary compat shim -- mirror patientAddress onto the
  // legacy spreeOrder.shippingAddress field so a backend that hasn't yet
  // deployed the patient address refactor still receives an address. Remove
  // once resolve_patient_address_input's shipping_address fallback is gone.
  if (orderState.spreeOrder) {
    orderState.spreeOrder.shippingAddress = orderState.patientAddress ?? null;
  }

  return orderState;
};

export const convertToWoundCareLineItemInput = (
  wounds: Wound[] | WoundInput[],
  woundCareLineItems: WoundCareLineItem[],
) => {
  const woundCareLineItemsCopy = cloneDeep(woundCareLineItems);
  const woundsCopy = cloneDeep(wounds);

  const fieldsToCompare: (keyof Pick<
    WoundCareLineItemInput,
    "productName" | "durationOfUse" | "frequencyOfUse" | "quantity"
  >)[] = ["productName", "durationOfUse", "frequencyOfUse", "quantity"];

  const getWoundIndex = (
    woundId: string | undefined,
    wounds: Wound[] | WoundInput[],
  ): number | null => {
    if (!woundId) return null;

    const index = wounds.findIndex(
      (wound, idx) =>
        ("id" in wound && wound.id?.toString() === woundId) ||
        idx.toString() === woundId,
    );

    return index !== -1 ? index : null;
  };

  const transformLineItem = (
    lineItem: WoundCareLineItem,
    wounds: Wound[] | WoundInput[],
  ): WoundCareLineItemInput => {
    const woundIndex = getWoundIndex(lineItem?.woundId?.toString(), wounds);
    const woundIndicesUsed = woundIndex !== null ? [woundIndex] : [];

    // Destructure to exclude fields not in WoundCareLineItemInput
    const {
      id,
      woundId,
      createdAt,
      updatedAt,
      woundPreferenceId,
      displayUnitLabel,
      ...rest
    } = lineItem;

    return {
      ...rest,
      formItemId: lineItem.formItemId ?? null,
      woundIndicesUsed,
      calculatedSizeData: null,
      selectedWoundsPreferenceIds: lineItem.woundPreferenceId
        ? [lineItem.woundPreferenceId]
        : [],
    };
  };

  const mergeMatches = (
    processedMatches: WoundCareLineItemInput[],
    lineItem: WoundCareLineItemInput,
  ) => {
    for (const processedMatch of processedMatches) {
      const isMatch =
        fieldsToCompare.every(
          (field) => lineItem[field] === processedMatch[field],
        ) && lineItem.size?.id === processedMatch.size?.id;
      if (isMatch) {
        processedMatch.woundIndicesUsed?.push(
          ...(lineItem.woundIndicesUsed || []),
        );
        if (
          lineItem.selectedWoundsPreferenceIds &&
          lineItem.selectedWoundsPreferenceIds.length === 1
        ) {
          processedMatch.selectedWoundsPreferenceIds?.push(
            lineItem.selectedWoundsPreferenceIds[0],
          );
        }
        return true;
      }
    }
    return false;
  };

  const mergedWoundCareLineItems: WoundCareLineItemInput[] = [];
  const uniqueLineItemNames = Array.from(
    new Set(
      woundCareLineItemsCopy
        .map((lineItem: any) => {
          delete lineItem.autoOop;
          delete lineItem.isMedicareWoundOop;
          delete lineItem.isSubstitutionOop;
          return lineItem;
        })
        .map((lineItem: WoundCareLineItemInput) => lineItem.productName || "")
        .filter((name) => name !== ""),
    ),
  );

  uniqueLineItemNames.forEach((uniqueName) => {
    const transformedLineItems: WoundCareLineItemInput[] =
      woundCareLineItemsCopy
        .filter((lineItem) => lineItem.productName === uniqueName)
        .map((lineItem) => transformLineItem(lineItem, woundsCopy));

    const processedMatches: WoundCareLineItemInput[] = [];
    transformedLineItems.forEach((lineItem) => {
      if (!mergeMatches(processedMatches, lineItem)) {
        processedMatches.push({
          ...lineItem,
          selectedWoundsPreferenceIds:
            lineItem.selectedWoundsPreferenceIds?.filter((id) => id != null),
        });
      }
    });

    mergedWoundCareLineItems.push(...processedMatches);
  });
  return mergedWoundCareLineItems;
};

/**
 * Converts an array of `WoundCareLineItemInput` objects into an array of `WoundCareLineItem` objects.
 * Note: Some fields have default values because they are not present in the input.
 */
export const convertToWoundCareLineItem = (
  woundCareLineItems: WoundCareLineItemInput[],
): WoundCareLineItem[] => {
  return woundCareLineItems.flatMap((lineItem, index) => {
    return (
      lineItem.woundIndicesUsed?.map((woundIndex, idx) => ({
        createdAt: null,
        durationOfUse: lineItem.durationOfUse ?? null,
        frequencyOfUse: lineItem.frequencyOfUse ?? null,
        id: String(index + idx),
        productName: lineItem.productName ?? "",
        productNameFromFax: lineItem.productNameFromFax ?? "",
        formItemId: lineItem.formItemId,
        quantity: lineItem.quantity ?? "",
        size: lineItem.size
          ? { id: lineItem.size?.id, name: lineItem.size?.name }
          : { id: null, name: "" },
        sendSmallerSize: lineItem.sendSmallerSize,
        updatedAt: null,
        woundId: woundIndex,
        sizePreferenceId: lineItem.sizePreferenceId,
        frequencyPreferenceId: lineItem.frequencyPreferenceId,
        woundPreferenceId: lineItem.selectedWoundsPreferenceIds?.[idx] ?? null,
        substitutionPreferenceId: lineItem.substitutionPreferenceId,
        isMedicareWoundOop: false,
        isSubstitutionOop: false,
      })) ?? []
    );
  });
};

/**
 * Form-item-variant ids for actual walkers (the "Default" subcategory),
 * excluding Canes/Crutches. Shared so the definition can't drift.
 */
export const getWalkerOnlyVariantIds = (
  departmentCategories: DepartmentCategories | null,
): Set<number> => {
  const walkerCategory = departmentCategories?.categories.find(
    (c) => c.identifier === Category.Walker,
  );
  const defaultSubcategory = walkerCategory?.subcategories.find(
    (s) => s.name === "Default",
  );
  return new Set(
    (defaultSubcategory?.formItems ?? []).flatMap((formItem) =>
      formItem.variants.map((v) => v.id),
    ),
  );
};

export const convertGraphQLOutputToInput = (
  providerReferral: any,
): ProviderReferralInput => {
  providerReferral = cloneDeep(providerReferral);

  delete providerReferral.id;
  delete providerReferral.createdAt;
  delete providerReferral.orderStatusUpdatedAt;
  delete providerReferral.allowedToEdit;
  delete providerReferral.assigned;
  // Output-only: `orderDisplayModels` exists on `ProviderReferral` but not on
  // `ProviderReferralInput`. This converter is a blacklist over a cloned query
  // result, so anything a caller's query selects and this doesn't delete is
  // spread straight into `createProviderReferral` and rejected by input
  // coercion. The ops GET_ORDER_QUERY selects it for the display-model table.
  delete providerReferral.orderDisplayModels;

  providerReferral["orderDocumentsInfo"] = [];
  delete providerReferral.orderDocuments;
  providerReferral["faxInput"] = initializeFaxInput();

  delete providerReferral.spreeOrder.id;
  delete providerReferral.spreeOrder.createdAt;
  delete providerReferral.spreeOrder.updatedAt;
  delete providerReferral.spreeOrder.completedAt;
  delete providerReferral.spreeOrder.userId;

  // `spreeOrder.shippingAddress` is output-only (this order's own resolved
  // ship-to address) — SpreeOrderInput has no such field. The patient's own
  // address for editing is normally the top-level `patientAddress` (see
  // resolve_patient_address_input()), but a draft's first save has no linked
  // patient yet, so `patientAddress` is null even though the order already
  // has a real address entered. Fall back to it so the edit form isn't blank.
  if (
    !providerReferral.patientAddress &&
    providerReferral.spreeOrder.shippingAddress
  ) {
    providerReferral.patientAddress =
      providerReferral.spreeOrder.shippingAddress;
  }
  delete providerReferral.spreeOrder.shippingAddress;

  if (providerReferral.patientAddress) {
    delete providerReferral.patientAddress.id;
    delete providerReferral.patientAddress.createdAt;
    delete providerReferral.patientAddress.updatedAt;
  }

  if (providerReferral.clinicDelivery?.address) {
    delete providerReferral.clinicDelivery.address.id;
    delete providerReferral.clinicDelivery.address.createdAt;
    delete providerReferral.clinicDelivery.address.updatedAt;
  }

  providerReferral.isMedicareBlockingEligible = false;

  const mergedWoundCareLineItems = convertToWoundCareLineItemInput(
    providerReferral.woundOrder.wounds,
    providerReferral.woundOrder.woundCareLineItems,
  );

  providerReferral = objectFieldSet(
    providerReferral,
    "woundOrder.woundCareLineItems",
    mergedWoundCareLineItems,
  );

  for (const lineItem of providerReferral.compressionOrder
    .compressionLineItems) {
    delete lineItem.id;
    delete lineItem.createdAt;
    delete lineItem.updatedAt;
  }

  for (const diagnosis of providerReferral.miscellaneousOrder.diagnoses) {
    delete diagnosis.id;
  }

  for (const lineItem of providerReferral.miscellaneousOrder.lineItems) {
    delete lineItem.id;
  }

  for (const [index, wound] of providerReferral.woundOrder.wounds.entries()) {
    delete wound.id;
    delete wound.departmentOrderId;
    wound.dimensionLength = wound.dimensionLength?.toString() ?? "";
    wound.dimensionWidth = wound.dimensionWidth?.toString() ?? "";
    wound.dimensionDepth = wound.dimensionDepth?.toString() ?? "";
    wound.index = index;
    if (!wound.traceId) {
      wound.traceId = crypto.randomUUID();
    }
  }

  const numWoundsOnOrder = providerReferral.woundOrder.wounds.length;
  const numWoundsToAdd = NUM_WOUNDS - numWoundsOnOrder;
  for (let i = 0; i < numWoundsToAdd; i++) {
    providerReferral.woundOrder.wounds.push(
      emptyWound(providerReferral.woundOrder.wounds.length),
    );
  }

  const numMiscellaneousDiagnosesToAdd =
    NUM_MISCELLANEOUS_DIAGNOSES -
    providerReferral.miscellaneousOrder.diagnoses.length;
  for (let i = 0; i < numMiscellaneousDiagnosesToAdd; i++) {
    providerReferral.miscellaneousOrder.diagnoses.push(
      initialMiscellaneousDiagnosis(),
    );
  }
  const numMiscellaneousLineItemsToAdd =
    NUM_MISCELLANEOUS_LINE_ITEMS -
    providerReferral.miscellaneousOrder.lineItems.length;
  for (let i = 0; i < numMiscellaneousLineItemsToAdd; i++) {
    providerReferral.miscellaneousOrder.lineItems.push(
      initialMiscellaneousLineItem(),
    );
  }

  for (const lineItem of providerReferral.careBridgeOrder.careBridgeLineItems) {
    delete lineItem.productName;
    delete lineItem.productType;
  }

  for (const lineItem of providerReferral.cpapOrder.cpapLineItems) {
    delete lineItem.id;
    delete lineItem.hcpcsCode;
    delete lineItem.createdAt;
    delete lineItem.updatedAt;
  }

  for (const diagnosis of providerReferral.cpapOrder.cpapDiagnoses) {
    delete diagnosis.id;
  }

  if (providerReferral.ventilatorOrder) {
    delete providerReferral.ventilatorOrder.id;
    for (const diagnosis of providerReferral.ventilatorOrder
      .ventilatorDiagnoses ?? []) {
      delete diagnosis.id;
    }
    // The whole array, not just its ids: ventilator line items are derived from
    // the two selections at insert time, so `VentilatorOrderInput` has no such
    // field and submitting one fails the mutation outright.
    delete providerReferral.ventilatorOrder.ventilatorLineItems;
  }
  if (providerReferral.nebulizerOrder) {
    delete providerReferral.nebulizerOrder.id;
    for (const diagnosis of providerReferral.nebulizerOrder
      .nebulizerDiagnoses ?? []) {
      delete diagnosis.id;
    }
    // The whole array, not just its ids: nebulizer line items are derived from
    // the equipment selections at insert time, so `NebulizerOrderInput` has no
    // such field and submitting one fails the mutation outright.
    delete providerReferral.nebulizerOrder.nebulizerLineItems;
  }

  if (providerReferral.wheelchairsAndScootersOrder) {
    delete providerReferral.wheelchairsAndScootersOrder.id;
    for (const diagnosis of providerReferral.wheelchairsAndScootersOrder
      .wheelchairsAndScootersDiagnoses ?? []) {
      delete diagnosis.id;
    }
    // Reshaped to the input's narrower shape (formItemId/productName only).
    // The device's own row rides along too (no distinguishing column);
    // insert skips it rather than trusting it (see `is_device_line_item`).
    providerReferral.wheelchairsAndScootersOrder.wheelchairsAndScootersLineItems =
      (
        providerReferral.wheelchairsAndScootersOrder
          .wheelchairsAndScootersLineItems ?? []
      ).map((item: any) => ({
        formItemId: item.formItemId,
        productName: item.productName,
      }));
  }

  if (providerReferral.diabetesOrder) {
    delete providerReferral.diabetesOrder.id;
    for (const diagnosis of providerReferral.diabetesOrder.diabetesDiagnoses ??
      []) {
      delete diagnosis.id;
    }
    // Reshaped to the input's narrower shape. The derived system rows ride
    // along too; insert drops and re-derives them (`is_derived_line_item`).
    providerReferral.diabetesOrder.diabetesLineItems = (
      providerReferral.diabetesOrder.diabetesLineItems ?? []
    ).map((item: any) => ({
      formItemId: item.formItemId,
      productName: item.productName,
      sizeId: item.sizeId,
      size: item.size,
      quantity: item.quantity,
      remarks: item.remarks,
      section: item.section,
    }));
  }

  if (providerReferral.woundSuctionOrder) {
    delete providerReferral.woundSuctionOrder.id;
    // Same as nebulizer above: the products are derived from the equipment
    // selections at insert time, so `WoundSuctionOrderInput` has no such field
    // and submitting one fails the mutation outright.
    delete providerReferral.woundSuctionOrder.woundSuctionLineItems;
    // Treatment plan lines carry ids on both the edit query and the Review &
    // Sign query (which SignSummaryModal converts for the mutation too), and
    // `WoundSuctionTreatmentInput` has no `id`.
    for (const treatment of providerReferral.woundSuctionOrder
      .woundSuctionTreatments ?? []) {
      delete treatment.id;
    }
  }

  // Shell form rows are read back with ids so the edit flow can hydrate them, but
  // ShellFormOrderInput has no `id`, so a leaked one fails the whole mutation with
  // "Field 'id' is not defined by type".
  //
  // `productCategory` needs the same treatment on LINE ITEMS but not on the order:
  // ShellFormOrder carries it on both, while only ShellFormOrderInput accepts it —
  // ShellFormLineItemInput does not. Leave it on the order (it says which form the
  // entry is) and strip it from each item.
  for (const shellFormOrder of providerReferral.shellFormOrders ?? []) {
    delete shellFormOrder.id;
    for (const lineItem of shellFormOrder.lineItems ?? []) {
      delete lineItem.id;
      delete lineItem.productCategory;
    }
    for (const diagnosis of shellFormOrder.diagnoses ?? []) {
      delete diagnosis.id;
    }
  }

  // Nested-row ids are server-only; without this, editing an order fails submit
  // with "Field 'id' is not defined by type ...". The order-level id isn't
  // selected by any query, so it needs no scrub.
  if (providerReferral.offTheShelfOrthoticsOrder) {
    for (const lineItem of providerReferral.offTheShelfOrthoticsOrder
      .offTheShelfOrthoticsLineItems ?? []) {
      delete lineItem.id;
      delete (lineItem as { sizeWithMeasurements?: unknown })
        .sizeWithMeasurements;
    }
    for (const diagnosis of providerReferral.offTheShelfOrthoticsOrder
      .offTheShelfOrthoticsDiagnoses ?? []) {
      delete diagnosis.id;
    }
  }

  // Same reason as the block above. `qualifyingGroups` needs no scrub: the input
  // type accepts it and the backend ignores it, precisely so this round trip works.
  if (providerReferral.customOrthoticsOrder) {
    for (const lineItem of providerReferral.customOrthoticsOrder
      .customOrthoticsLineItems ?? []) {
      delete lineItem.id;
    }
    for (const diagnosis of providerReferral.customOrthoticsOrder
      .customOrthoticsDiagnoses ?? []) {
      delete diagnosis.id;
    }
  }

  // Same reason as the two blocks above.
  if (providerReferral.prostheticsOrder) {
    for (const lineItem of providerReferral.prostheticsOrder
      .prostheticsLineItems ?? []) {
      delete lineItem.id;
      delete lineItem.hcpcsCode;
    }
    for (const diagnosis of providerReferral.prostheticsOrder
      .prostheticsDiagnoses ?? []) {
      delete diagnosis.id;
    }
  }

  delete providerReferral.planOfCares;
  delete providerReferral.diagnoses;
  delete providerReferral.modernLineItems;
  delete providerReferral.orderRemarks;
  delete providerReferral.orderNotes;
  delete providerReferral.orderProductSummary;
  delete providerReferral.signerName;
  delete providerReferral.signerInfo;
  delete providerReferral.isHandOff;
  delete providerReferral.createdBy;

  providerReferral.isFaxOrder = providerReferral.isFaxOrder ?? false;
  providerReferral.isHighCostFlaggingEligible =
    providerReferral.isHighCostFlaggingEligible ?? false;
  providerReferral.uploadProgressNoteNow =
    providerReferral.uploadProgressNoteNow ?? false;

  const transcriptionNote = providerReferral.transcriptionInternalNote;
  if (
    transcriptionNote != null &&
    typeof transcriptionNote === "object" &&
    "note" in transcriptionNote
  ) {
    providerReferral.transcriptionInternalNote =
      (transcriptionNote as { note?: string | null }).note ?? null;
  }

  traverse(providerReferral, removeTypenameTraverser);

  return providerReferral;
};

export const loadModernFormOrder = (
  departmentCategories: DepartmentCategories | null,
  // Rows pass through unchanged. Training-order copies don't select
  // deliveryLocationType, so the copy doesn't inherit the source clinic's.
  planOfCares: Omit<PlanOfCare, "deliveryLocationType">[],
  setPlanOfCares: (value: PlanOfCareInput[]) => void,
  diagnoses: Diagnosis[],
  setDiagnoses: (value: DiagnosisInput[]) => void,
  modernLineItems: ModernLineItem[],
  setModernLineItems: (value: ModernLineItemInput[]) => void,
) => {
  if (!departmentCategories) {
    return;
  }

  const planOfCareInputs: PlanOfCareInput[] = [];
  for (const planOfCare of planOfCares as any) {
    delete planOfCare.__typename;
    delete planOfCare.id;

    traverse(planOfCare, removeTypenameTraverser);
    planOfCareInputs.push(planOfCare as PlanOfCareInput);
  }
  setPlanOfCares(planOfCareInputs);

  const diagnosisInputs: DiagnosisInput[] = [];
  for (const diagnosis of diagnoses as any) {
    delete diagnosis.__typename;
    delete diagnosis.id;

    traverse(diagnosis, removeTypenameTraverser);
    diagnosisInputs.push(diagnosis as DiagnosisInput);
  }
  const diagnosisInputsWithBlanks = initializeBlankCustomDiagnoses(
    departmentCategories,
    diagnosisInputs,
  );
  setDiagnoses(diagnosisInputsWithBlanks);

  setModernLineItems(
    modernLineItems.map((modernLineItem) => {
      const cleanedItem = {
        categoryId: modernLineItem.categoryId,
        formItemVariantId: modernLineItem.formItemVariantId,
        customProductName: modernLineItem.customProductName,
        pumpRateMlPerHour: modernLineItem.pumpRateMlPerHour,
        pumpHours: modernLineItem.pumpHours,
        referenceNumber: modernLineItem.referenceNumber,
        quantity: modernLineItem.quantity,
        // Preserve the incontinence "Maximum allowed amount" selection so editing
        // a saved order keeps it and doesn't trip the submit-time quantity gate.
        quantityIsMaximum: modernLineItem.quantityIsMaximum,
        bottlesPerMonth: modernLineItem.bottlesPerMonth,
      };

      traverse(cleanedItem, removeTypenameTraverser);
      return cleanedItem;
    }),
  );
};

export const loadModernFormOrderRHF = (
  departmentCategories: DepartmentCategories | null,
  planOfCares: PlanOfCare[],
  diagnoses: Diagnosis[],
  modernLineItems: ModernLineItem[],
) => {
  const planOfCareInputs: PlanOfCareInput[] = [];
  for (const planOfCare of planOfCares as any) {
    delete planOfCare.__typename;
    delete planOfCare.id;

    traverse(planOfCare, removeTypenameTraverser);
    planOfCareInputs.push(planOfCare as PlanOfCareInput);
  }

  const diagnosisInputs: DiagnosisInput[] = [];
  for (const diagnosis of diagnoses as any) {
    delete diagnosis.__typename;
    delete diagnosis.id;

    traverse(diagnosis, removeTypenameTraverser);
    diagnosisInputs.push(diagnosis as DiagnosisInput);
  }

  const diagnosisInputsWithBlanks = initializeBlankCustomDiagnoses(
    departmentCategories,
    diagnosisInputs,
  );

  const modernLineItemInputs: ModernLineItemInput[] = modernLineItems.map(
    (modernLineItem) => {
      const cleanedItem: ModernLineItemInput = {
        categoryId: modernLineItem.categoryId,
        formItemVariantId: modernLineItem.formItemVariantId,
        customProductName: modernLineItem.customProductName,
        bottlesPerMonth: modernLineItem.bottlesPerMonth,
        pumpRateMlPerHour: modernLineItem.pumpRateMlPerHour,
        pumpHours: modernLineItem.pumpHours,
        referenceNumber: modernLineItem.referenceNumber,
        quantity: modernLineItem.quantity,
        // This loader runs over every modern line item (not just RHF
        // categories), and its output is merged into providerReferralInput.
        // Carry the incontinence "Maximum allowed amount" flag so the snapshot
        // doesn't drop it after it was loaded from GET_ORDER.
        quantityIsMaximum: modernLineItem.quantityIsMaximum,
      };

      traverse(cleanedItem, removeTypenameTraverser);
      return cleanedItem;
    },
  );

  return {
    planOfCareInputs,
    diagnosisInputs: diagnosisInputsWithBlanks,
    modernLineItemInputs,
  };
};

export function getCategoryDiagnosisTypeIdToDiagnosisTypeMap(
  departmentCategories: DepartmentCategories | null,
): Record<number, DiagnosisType> {
  if (!departmentCategories) {
    return {};
  }
  return departmentCategories.categories.reduce(
    (acc, category) => {
      category.categoryDiagnosisTypes.forEach((categorydiagnosisType) => {
        acc[categorydiagnosisType.id] = categorydiagnosisType.diagnosisType;
      });
      return acc;
    },
    {} as Record<number, DiagnosisType>,
  );
}

export const sanitizeDiagnosisInputs = (
  diagnoses: DiagnosisInput[],
  categoryDiagnosisTypeIdToDiagnosisTypeMap: Record<number, DiagnosisType>,
): DiagnosisInput[] => {
  const sanitizedDiagnosisInputs: DiagnosisInput[] = [];
  diagnoses.map((diagnosis) => {
    const diagnosisType =
      categoryDiagnosisTypeIdToDiagnosisTypeMap[
        diagnosis.categoryDiagnosisTypeId
      ];
    if (diagnosisType?.type === DiagnosisTypeEnum.Icd10Diagnosis) {
      if (diagnosis.icd10DescriptionId && diagnosis.customIcd10Description) {
        sanitizedDiagnosisInputs.push({
          ...diagnosis,
          customIcd10Description: null,
          customIcd10Code: null,
        });
      } else if (
        diagnosis.icd10DescriptionId !== null ||
        diagnosis.customIcd10Description !== null
      ) {
        sanitizedDiagnosisInputs.push(diagnosis);
      }
    }
  });

  return sanitizedDiagnosisInputs;
};

export const addressInfoToString = (selectedDepartmentInfo: Department) => {
  let address = "";
  if (selectedDepartmentInfo) {
    if (
      selectedDepartmentInfo.address1 &&
      selectedDepartmentInfo.city &&
      selectedDepartmentInfo.stateAbbr &&
      selectedDepartmentInfo.zipcode
    ) {
      address += selectedDepartmentInfo.address1;
      if (selectedDepartmentInfo.address2) {
        address += ", " + selectedDepartmentInfo.address2;
      }
      address += ", " + selectedDepartmentInfo.city;
      address += ", " + selectedDepartmentInfo.stateAbbr;
      address += " " + selectedDepartmentInfo.zipcode;
    }
  }
  return address;
};

export const orderStateWithModernCategories = (
  orderState: ProviderReferralInput,
  planOfCares: PlanOfCareInput[],
  diagnoses: DiagnosisInput[],
  modernLineItems: ModernLineItemInput[],
  categoryDiagnosisTypeIdToDiagnosisTypeMap: Record<number, DiagnosisType>,
): ProviderReferralInput => {
  const newOrderState = cloneDeep(orderState);

  newOrderState.planOfCareInputs = planOfCares;
  newOrderState.diagnosisInputs = sanitizeDiagnosisInputs(
    diagnoses,
    categoryDiagnosisTypeIdToDiagnosisTypeMap,
  );
  newOrderState.modernLineItemInputs = modernLineItems;

  return newOrderState;
};
